import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  format,
  isValid
} from 'date-fns';
import { es } from 'date-fns/locale';
// Eliminado uso de googleApi y metaApi; todo pasa por analyticsApi (combined)
import analyticsApi, { fetchCombinedAnalyticsSummaryNoCache } from 'api/analytics';
import { useAuth } from 'components/context/AuthContext';
import {
  ArrowUp,
  ArrowDown,
  MousePointerClick,
  DollarSign,
  Target,
  Eye,
  Users,
  TrendingDown,
  Percent,
  TrendingUp,
  Download,
  ArrowUpDown
} from 'lucide-react';
import AnalyticsMetricCard from 'components/analytics/AnalyticsMetricCard';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import NoAdsConnectedSection from 'components/common/NoAdsConnectedSection';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { useMetaAds } from 'hooks/useMetaAds';
import { useAnalyticsFilter } from 'context/AnalyticsFilterContext';
import AnalyticsStickyFilterBar from 'components/analytics/AnalyticsStickyFilterBar';
import { calculateDateRange } from 'utils/dateRangeUtils';
import CampaignsTableSkeleton from 'components/ui/CampaignsTableSkeleton';
import PlatformTableSkeleton from 'components/ui/PlatformTableSkeleton';
import EvolutionChartSkeleton from 'components/ui/EvolutionChartSkeleton';
import RoasDistributionChartsSkeleton from 'components/ui/RoasDistributionChartsSkeleton';
import MobileBottomSheet from 'components/ui/MobileBottomSheet';
import { useResponsive } from 'hooks/useResponsive';
const CARD_METRICS_STORAGE_KEY = 'analytics_card_metrics';
const CARD_METRICS_VERSION_KEY = 'analytics_card_metrics_version';
const CARD_METRICS_STORAGE_VERSION = 2;

const DEFAULT_CARD_METRICS_V1 = [
  'Clicks',
  'Costo',
  'CPC',
  'Impresiones',
  'Conversiones',
  'CPA',
  'CTR',
  'ROAS'
];

const DEFAULT_CARD_METRICS_V2 = [
  'Costo',
  'Conversiones',
  'Clicks',
  'Impresiones',
  'ROAS',
  'CPA',
  'CPC',
  'CTR'
];

const arraysEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

const loadCardMetricsFromStorage = () => {
  if (typeof window === 'undefined') return DEFAULT_CARD_METRICS_V2;
  try {
    const storedVersion = Number(window.localStorage.getItem(CARD_METRICS_VERSION_KEY)) || 1;
    const storedValue = window.localStorage.getItem(CARD_METRICS_STORAGE_KEY);
    if (!storedValue) return DEFAULT_CARD_METRICS_V2;

    const parsed = JSON.parse(storedValue);
    if (!Array.isArray(parsed)) return DEFAULT_CARD_METRICS_V2;

    const hasOnlyKnownMetrics = parsed.every((metricId) => DEFAULT_CARD_METRICS_V2.includes(metricId));
    if (!hasOnlyKnownMetrics) return DEFAULT_CARD_METRICS_V2;

    if (storedVersion < CARD_METRICS_STORAGE_VERSION && arraysEqual(parsed, DEFAULT_CARD_METRICS_V1)) {
      return DEFAULT_CARD_METRICS_V2;
    }

    return parsed;
  } catch (error) {
    console.warn('No se pudo leer el orden de métricas guardado', error);
    return DEFAULT_CARD_METRICS_V2;
  }
};

const formatDate = (date, granularity = 'daily') => {
  // Validar que la fecha sea válida antes de formatear
  if (!date || !isValid(date)) {
    console.warn('Fecha inválida en formatDate:', date);
    return 'N/A';
  }
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  if (granularity === 'weekly') {
    return format(utcDate, 'dd/MM', { locale: es });
  } else if (granularity === 'monthly') {
    return format(utcDate, 'MMMM', { locale: es });
  } else if (granularity === 'full-year-day') {
    return format(utcDate, 'yyyy-MM-dd', { locale: es });
  } else if (granularity === 'full-year-short') {
    return format(utcDate, 'dd/MM/yy', { locale: es });
  }
  return format(utcDate, 'dd/MM', { locale: es });
};

// Convierte valores potencialmente string/number/etc a número finito (o null si no se puede)
const coerceToNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return null;

    // Quitar símbolos/espacios y preservar solo dígitos, separadores y signo
    const cleaned = raw.replace(/[^\d.,\-+]/g, '');

    // Soportar ambos formatos:
    // - US: "1,234.56" ("," miles, "." decimal)
    // - EU: "1.234,56" ("." miles, "," decimal)
    // Heurística: el último separador (entre '.' y ',') es el decimal; el otro se considera de miles.
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    let normalized = cleaned;
    if (lastComma !== -1 && lastDot !== -1) {
      const decimalSep = lastComma > lastDot ? ',' : '.';
      const thousandSep = decimalSep === ',' ? '.' : ',';
      normalized =
        (thousandSep === '.' ? normalized.replace(/\./g, '') : normalized.replace(/,/g, ''))
          .replace(decimalSep, '.');
    } else if (lastComma !== -1) {
      // Solo coma presente: asumir coma decimal si no parece miles (e.g. "1,23"), si no, remover como miles.
      const digitsAfter = cleaned.length - lastComma - 1;
      normalized = digitsAfter === 3 ? cleaned.replace(/,/g, '') : cleaned.replace(',', '.');
    } else if (lastDot !== -1) {
      // Solo punto presente: si parece miles (e.g. "1.234"), remover; caso contrario dejar como decimal.
      const digitsAfter = cleaned.length - lastDot - 1;
      normalized = digitsAfter === 3 ? cleaned.replace(/\./g, '') : cleaned;
    }

    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  }

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Convierte un monto a la moneda de visualización usando tasas de la DB (rate_to_usd: 1 unidad = X USD).
 * @param {number} value - Monto a convertir
 * @param {string} fromCurrency - Moneda del valor (ej. 'USD', 'UYU')
 * @param {string} toCurrency - Moneda objetivo (ej. 'USD')
 * @param {Record<string, number>} rates - Mapa de tasas desde API (ej. { USD: 1, UYU: 0.025 })
 * @returns {number} Valor en toCurrency, o value si falta tasa
 */
function convertToDisplayCurrency(value, fromCurrency, toCurrency, rates = {}) {
  if (value == null || !Number.isFinite(Number(value))) return 0;
  const num = Number(value);
  if (fromCurrency === toCurrency) return num;
  const rFrom = rates[fromCurrency];
  const rTo = rates[toCurrency] ?? (toCurrency === 'USD' ? 1 : undefined);
  if (rFrom == null || rTo == null) return num;
  return num * (rFrom / rTo);
}

// Agrega filas de métricas (campaign_metrics) y devuelve totales + ROAS (equivalente a _aggregate_rows del backend)
function aggregateCampaignRows(rows) {
  if (!rows || rows.length === 0) {
    return { investment: 0, clicks: 0, impressions: 0, conversions: 0, conversions_value: 0, roas: 0, activeCampaigns: 0 };
  }
  const impressions = rows.reduce((s, r) => s + (Number(r.impressions) || 0), 0);
  const clicks = rows.reduce((s, r) => s + (Number(r.clicks) || 0), 0);
  const cost = rows.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const conversions = rows.reduce((s, r) => s + (Number(r.conversions) || 0), 0);
  const conversions_value = rows.reduce((s, r) => s + (Number(r.conversions_value) || 0), 0);
  const activeCampaigns = new Set(rows.map(r => r.campaign_id).filter(Boolean)).size;
  const roas = cost > 0 && conversions_value > 0 ? conversions_value / cost : 0;
  return {
    investment: cost,
    cost,
    clicks,
    impressions,
    conversions,
    conversions_value,
    roas,
    activeCampaigns
  };
}

// Agrupa filas diarias (date + campaign_id + métricas) por campaign_id y devuelve campaign_metrics
function aggregateByCampaign(rows) {
  if (!rows || rows.length === 0) return [];
  const byId = new Map();
  for (const r of rows) {
    const id = String(r.campaign_id ?? '');
    if (!id) continue;
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, {
        campaign_id: id,
        campaign_name: r.campaign_name ?? r.name,
        name: r.campaign_name ?? r.name,
        status: r.status,
        channel_type: r.channel_type,
        bidding_strategy: r.bidding_strategy,
        daily_budget: r.daily_budget,
        impressions: Number(r.impressions) || 0,
        clicks: Number(r.clicks) || 0,
        cost: Number(r.cost) || 0,
        conversions: Number(r.conversions) || 0,
        conversions_value: Number(r.conversions_value) || 0,
        ctr: 0,
        average_cpc: 0,
        cpm: 0,
        cost_per_conversion: 0,
        value_per_conversion: 0,
        search_impression_share: r.search_impression_share,
        search_rank_lost_impression_share: r.search_rank_lost_impression_share,
        search_budget_lost_impression_share: r.search_budget_lost_impression_share,
        search_absolute_top_impression_share: r.search_absolute_top_impression_share,
        roi: 0,
        conversion_rate: 0
      });
    } else {
      existing.impressions += Number(r.impressions) || 0;
      existing.clicks += Number(r.clicks) || 0;
      existing.cost += Number(r.cost) || 0;
      existing.conversions += Number(r.conversions) || 0;
      existing.conversions_value += Number(r.conversions_value) || 0;
    }
  }
  const result = Array.from(byId.values());
  result.forEach((row) => {
    if (row.impressions > 0) row.ctr = (row.clicks / row.impressions) * 100;
    if (row.clicks > 0) row.average_cpc = row.cost / row.clicks;
    if (row.impressions > 0) row.cpm = (row.cost * 1000) / row.impressions;
    if (row.conversions > 0) {
      row.cost_per_conversion = row.cost / row.conversions;
      row.value_per_conversion = row.conversions_value / row.conversions;
    }
    if (row.clicks > 0) row.conversion_rate = (row.conversions / row.clicks) * 100;
    if (row.cost > 0) row.roi = ((row.conversions_value - row.cost) / row.cost) * 100;
  });
  return result;
}

// Componentes de logos oficiales de las plataformas
const GoogleAdsLogo = () => (
  <div className="flex items-center gap-2">
    <img 
      src="/google-ads-icon.png" 
      alt="Google Ads" 
      className="w-6 h-6"
    />
    <span className="text-sm font-medium text-white">Google Ads</span>
  </div>
);

const MetaAdsLogo = () => (
  <div className="flex items-center gap-2">
    <img 
      src="/meta-ads-icon.png" 
      alt="Meta Ads" 
      className="w-6 h-6"
    />
    <span className="text-sm font-medium text-white">Meta Ads</span>
  </div>
);

// Componente Tabla de Campañas con Impresiones (jerárquico: campañas → grupos → anuncios, carga lazy)
const CampaignsWithImpressionsTable = ({
  tableData,
  tableDataAdGroups,
  tableDataAds,
  onRequestAdGroups,
  onRequestAds,
  onApplyFilter,
  onClearFilter,
  hasCampaignFilter = false,
  getFullDataForExport,
  displayCurrency,
  exchangeRates = {},
  isGoogleConnected,
  isMetaConnected,
  loading: parentLoading,
  loadingAdGroups,
  loadingAds,
  filterConversionIds = [],
  filterConversionTypes = [],
  hasConversionFilter = false,
  hasGoogleConversionFilter = false,
  hasMetaConversionFilter = false
}) => {
  const [sortColumn, setSortColumn] = useState('cost');
  const [sortDirection, setSortDirection] = useState('desc');
  const [exportingCSV, setExportingCSV] = useState(false);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [selectedCampaignIdsForFilter, setSelectedCampaignIdsForFilter] = useState(() => new Set());
  const [showSortSheet, setShowSortSheet] = useState(false);
  const { isMobile } = useResponsive();

  // Función unificada para normalizar datos de cualquier nivel
  const normalizeMetrics = (entity, level) => {
    const base = {
      cost: entity.cost || entity.total_cost || 0,
      clicks: entity.clicks || 0,
      impressions: entity.impressions || 0,
      conversions: entity.conversions || 0,
      conversions_value: entity.conversions_value || 0,
      roas: entity.roas !== null && entity.roas !== undefined ? entity.roas : null
    };

    switch(level) {
      case 'campaign':
        return {
          ...base,
          name: entity.campaign_name || entity.name || 'Sin nombre',
          entityId: entity.campaign_id,
          entityType: 'campaign'
        };
      case 'ad_group':
        return {
          ...base,
          name: entity.ad_group_name || entity.adset_name || 'Sin nombre',
          entityId: entity.ad_group_id || entity.adset_id,
          entityType: 'ad_group',
          campaignName: entity.campaign_name,
          campaignId: entity.campaign_id
        };
      case 'ad':
        return {
          ...base,
          name: entity.ad_name || entity.name || 'Sin nombre',
          entityId: entity.ad_id,
          entityType: 'ad',
          adGroupId: entity.ad_group_id || entity.adset_id,
          adGroupName: entity.ad_group_name || entity.adset_name,
          campaignName: entity.campaign_name,
          campaignId: entity.campaign_id
        };
      default:
        return base;
    }
  };

  // Función para calcular métricas derivadas
  const calculateDerivedMetrics = (campaign) => {
    const cost = campaign.cost || 0;
    const clicks = campaign.clicks || 0;
    const impressions = campaign.impressions || 0;
    const conversions = campaign.conversions || 0;
    const conversions_value = campaign.conversions_value || 0;

    // Calcular ROAS si no viene directamente y tenemos los datos necesarios
    let roas = coerceToNumber(campaign.roas);
    if (roas === null && cost > 0 && conversions_value > 0) {
      roas = conversions_value / cost;
    }

    return {
      ...campaign,
      ctr: impressions > 0 ? Number(((clicks / impressions) * 100).toFixed(2)) : 0,
      cpc: clicks > 0 ? Number((cost / clicks).toFixed(2)) : 0,
      cpa: conversions > 0 ? Number((cost / conversions).toFixed(2)) : 0,
      cpm: impressions > 0 ? Number(((cost / impressions) * 1000).toFixed(2)) : 0,
      roas: roas !== null ? Number(roas.toFixed(2)) : null
    };
  };

  // Formatear moneda; convierte a displayCurrency usando tasas de la API (sin hardcode)
  const rates = useMemo(() => ({ USD: 1, ...exchangeRates }), [exchangeRates]);
  const formatCurrency = (value, currency = 'USD') => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '$0.00';
    const targetCurrency = displayCurrency || 'USD';
    const sourceCurrency = currency || targetCurrency;
    const canConvert =
      sourceCurrency !== targetCurrency &&
      rates[sourceCurrency] != null &&
      rates[targetCurrency] != null;
    const valueToFormat = canConvert
      ? convertToDisplayCurrency(value, sourceCurrency, targetCurrency, rates)
      : value;
    const currencyForFormat = canConvert ? targetCurrency : sourceCurrency;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyForFormat,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valueToFormat);
  };

  const campaignsData = useMemo(() => {
    const sortByColumn = (a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      if (aValue === null || aValue === undefined) aValue = 0;
      if (bValue === null || bValue === undefined) bValue = 0;
      aValue = typeof aValue === 'number' ? aValue : parseFloat(aValue) || 0;
      bValue = typeof bValue === 'number' ? bValue : parseFloat(bValue) || 0;
      if (['cost', 'cpa', 'cpc', 'cpm'].includes(sortColumn)) {
        const toCur = displayCurrency || 'USD';
        aValue = convertToDisplayCurrency(aValue, a.originalCurrency || 'USD', toCur, rates);
        bValue = convertToDisplayCurrency(bValue, b.originalCurrency || 'USD', toCur, rates);
      }
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    };

    if (!tableData) return [];
    const googleRaw = tableData?.google?.campaign_metrics || [];
    const metaRaw = tableData?.meta?.campaign_metrics || [];
    const googleMetrics = isGoogleConnected
      ? googleRaw.map(m => ({
          ...calculateDerivedMetrics(normalizeMetrics(m, 'campaign')),
          platform: 'google',
          platformLogo: <GoogleAdsLogo />,
          originalCurrency: tableData?.google?.account_currency || tableData?.display_currency || 'USD'
        }))
      : [];
    const metaMetrics = isMetaConnected
      ? metaRaw.map(m => ({
          ...calculateDerivedMetrics(normalizeMetrics(m, 'campaign')),
          platform: 'meta',
          platformLogo: <MetaAdsLogo />,
          originalCurrency: tableData?.meta?.account_currency || tableData?.display_currency || 'USD'
        }))
      : [];
    const all = [...googleMetrics, ...metaMetrics];
    return all.filter(m => (m.impressions || 0) > 0).sort(sortByColumn);
  }, [tableData, isGoogleConnected, isMetaConnected, sortColumn, sortDirection, displayCurrency, rates]);

  // IDs de campañas presentes en la tabla (para filtrar grupos/anuncios por el mismo criterio)
  const campaignIdsSet = useMemo(() => {
    if (!tableData) return new Set();
    const ids = new Set();
    (tableData?.google?.campaign_metrics || []).forEach(m => { if (m.campaign_id != null) ids.add(String(m.campaign_id)); });
    (tableData?.meta?.campaign_metrics || []).forEach(m => { if (m.campaign_id != null) ids.add(String(m.campaign_id)); });
    return ids;
  }, [tableData]);

  // Helpers de normalización de conversiones (usados en ad groups y ads)
  const normalizeGoogleConversionActionId = useCallback((value) => {
    if (value == null) return null;
    const s = String(value).trim();
    if (!s) return null;
    if (s.includes('/')) return s.split('/').pop();
    return s;
  }, []);
  const metaAllowedActionTypes = useCallback((conversionTypes) => {
    if (!conversionTypes || conversionTypes.length === 0) return null;
    const allowed = new Set();
    conversionTypes.forEach((c) => {
      const s = String(c || '').trim();
      if (!s) return;
      const underscoreIndex = s.indexOf('_');
      const prefix = underscoreIndex >= 0 ? s.slice(0, underscoreIndex) : '';
      if (underscoreIndex >= 0 && /^\d+$/.test(prefix)) {
        allowed.add(s.slice(underscoreIndex + 1).toLowerCase());
      } else {
        allowed.add(s.toLowerCase());
      }
    });
    return allowed.size ? allowed : null;
  }, []);
  const metaCanonicalFromActionType = useCallback((actionType) => {
    let at = (actionType || '').trim().toLowerCase();
    if (!at) return '';
    if (!at.includes('.')) return at;
    const lastSegment = at.split('.').pop();
    if (lastSegment.includes('_')) return lastSegment.split('_').pop();
    return lastSegment;
  }, []);

  // Mapas de conversions_detail por entidad para ad groups (desde backend)
  const adGroupDetailByEntityId = useMemo(() => {
    const google = new Map();
    const meta = new Map();
    (tableDataAdGroups?.google?.ad_group_conversions_detail || []).forEach((ag) => {
      const id = String(ag.ad_group_id ?? '');
      if (id) google.set(id, ag.conversions_detail || []);
    });
    (tableDataAdGroups?.meta?.adset_conversions_detail || []).forEach((a) => {
      const id = String(a.adset_id ?? '');
      if (id) meta.set(id, a.conversions_detail || []);
    });
    return { google, meta };
  }, [tableDataAdGroups]);

  // Aplicar filtro de conversiones a una fila de ad group (muta row)
  const applyConversionFilterToAdGroupRow = useCallback(
    (row) => {
      const entityId = String(row.entityId ?? '');
      if (!entityId) return;
      const details = row.platform === 'google' ? adGroupDetailByEntityId.google.get(entityId) : adGroupDetailByEntityId.meta.get(entityId);
      if (!details || details.length === 0) return;

      if (row.platform === 'google' && hasGoogleConversionFilter) {
        const allowedIds = new Set((filterConversionIds || []).map((id) => normalizeGoogleConversionActionId(id)).filter(Boolean));
        let convC = 0;
        let convV = 0;
        details.forEach((d) => {
          const caId = normalizeGoogleConversionActionId(d.conversion_action_id);
          if (caId && allowedIds.has(caId)) {
            convC += Number(d.conversions ?? 0);
            convV += Number(d.conversions_value ?? 0);
          }
        });
        row.conversions = convC;
        row.conversions_value = convV;
      } else if (row.platform === 'meta' && hasMetaConversionFilter) {
        const allowedActionTypes = metaAllowedActionTypes(filterConversionTypes);
        if (!allowedActionTypes || allowedActionTypes.size === 0) return;
        const canonicalAllowed = new Set(Array.from(allowedActionTypes).filter((a) => !a.includes('.')));
        let convC = 0;
        let convV = 0;
        details.forEach((d) => {
          const raw = (d.conversion_action_id || d.conversion_action_name || '').toString().toLowerCase();
          if (!raw) return;
          if (!raw.includes('.')) {
            if (allowedActionTypes.has(raw)) {
              convC += Number(d.conversions ?? 0);
              convV += Number(d.conversions_value ?? 0);
            }
            return;
          }
          if (!allowedActionTypes.has(raw)) return;
          const canonical = metaCanonicalFromActionType(raw);
          if (canonical && canonicalAllowed.has(canonical)) return;
          convC += Number(d.conversions ?? 0);
          convV += Number(d.conversions_value ?? 0);
        });
        row.conversions = convC;
        row.conversions_value = convV;
      }
      const cost = Number(row.cost ?? 0);
      row.cpa = row.conversions > 0 ? cost / row.conversions : 0;
      row.roas = cost > 0 && row.conversions_value > 0 ? row.conversions_value / cost : null;
    },
    [
      adGroupDetailByEntityId,
      hasGoogleConversionFilter,
      hasMetaConversionFilter,
      filterConversionIds,
      filterConversionTypes,
      normalizeGoogleConversionActionId,
      metaAllowedActionTypes,
      metaCanonicalFromActionType,
    ]
  );

  const adGroupsBaseData = useMemo(() => {
    if (!tableDataAdGroups) return [];
    const googleRaw = (tableDataAdGroups?.google?.ad_group_metrics || []).map((m) => ({
      ...calculateDerivedMetrics(normalizeMetrics(m, 'ad_group')),
      platform: 'google',
      platformLogo: <GoogleAdsLogo />,
      originalCurrency: tableDataAdGroups?.google?.account_currency || 'USD',
    }));
    const metaRaw = (tableDataAdGroups?.meta?.adset_metrics || []).map((m) => ({
      ...calculateDerivedMetrics(normalizeMetrics(m, 'ad_group')),
      platform: 'meta',
      platformLogo: <MetaAdsLogo />,
      originalCurrency: tableDataAdGroups?.meta?.account_currency || 'USD',
    }));
    let all = [...googleRaw, ...metaRaw].filter((m) => (m.impressions || 0) > 0);
    if (hasConversionFilter) {
      all.forEach((row) => applyConversionFilterToAdGroupRow(row));
    }
    return all;
  }, [tableDataAdGroups, hasConversionFilter, applyConversionFilterToAdGroupRow]);

  const adGroupsFlatData = useMemo(() => {
    const sortByColumn = (a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      if (aValue === null || aValue === undefined) aValue = 0;
      if (bValue === null || bValue === undefined) bValue = 0;
      aValue = typeof aValue === 'number' ? aValue : parseFloat(aValue) || 0;
      bValue = typeof bValue === 'number' ? bValue : parseFloat(bValue) || 0;
      if (['cost', 'cpa', 'cpc', 'cpm'].includes(sortColumn)) {
        const toCur = displayCurrency || 'USD';
        aValue = convertToDisplayCurrency(aValue, a.originalCurrency || 'USD', toCur, rates);
        bValue = convertToDisplayCurrency(bValue, b.originalCurrency || 'USD', toCur, rates);
      }
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    };
    let all = [...adGroupsBaseData];
    if (campaignIdsSet.size > 0) {
      all = all.filter((g) => campaignIdsSet.has(String(g.campaignId ?? '')));
    }
    return [...all].sort(sortByColumn);
  }, [adGroupsBaseData, campaignIdsSet, sortColumn, sortDirection, displayCurrency, rates]);

  // Mapas de conversions_detail por entidad para ads (desde backend)
  const adDetailByEntityId = useMemo(() => {
    const google = new Map();
    const meta = new Map();
    (tableDataAds?.google?.ad_conversions_detail || []).forEach((ad) => {
      const id = String(ad.ad_id ?? '');
      if (id) google.set(id, ad.conversions_detail || []);
    });
    (tableDataAds?.meta?.ad_conversions_detail || []).forEach((ad) => {
      const id = String(ad.ad_id ?? '');
      if (id) meta.set(id, ad.conversions_detail || []);
    });
    return { google, meta };
  }, [tableDataAds]);

  const applyConversionFilterToAdRow = useCallback(
    (row) => {
      const entityId = String(row.entityId ?? '');
      if (!entityId) return;
      const details = row.platform === 'google' ? adDetailByEntityId.google.get(entityId) : adDetailByEntityId.meta.get(entityId);
      if (!details || details.length === 0) return;

      if (row.platform === 'google' && hasGoogleConversionFilter) {
        const allowedIds = new Set((filterConversionIds || []).map((id) => normalizeGoogleConversionActionId(id)).filter(Boolean));
        let convC = 0;
        let convV = 0;
        details.forEach((d) => {
          const caId = normalizeGoogleConversionActionId(d.conversion_action_id);
          if (caId && allowedIds.has(caId)) {
            convC += Number(d.conversions ?? 0);
            convV += Number(d.conversions_value ?? 0);
          }
        });
        row.conversions = convC;
        row.conversions_value = convV;
      } else if (row.platform === 'meta' && hasMetaConversionFilter) {
        const allowedActionTypes = metaAllowedActionTypes(filterConversionTypes);
        if (!allowedActionTypes || allowedActionTypes.size === 0) return;
        const canonicalAllowed = new Set(Array.from(allowedActionTypes).filter((a) => !a.includes('.')));
        let convC = 0;
        let convV = 0;
        details.forEach((d) => {
          const raw = (d.conversion_action_id || d.conversion_action_name || '').toString().toLowerCase();
          if (!raw) return;
          if (!raw.includes('.')) {
            if (allowedActionTypes.has(raw)) {
              convC += Number(d.conversions ?? 0);
              convV += Number(d.conversions_value ?? 0);
            }
            return;
          }
          if (!allowedActionTypes.has(raw)) return;
          const canonical = metaCanonicalFromActionType(raw);
          if (canonical && canonicalAllowed.has(canonical)) return;
          convC += Number(d.conversions ?? 0);
          convV += Number(d.conversions_value ?? 0);
        });
        row.conversions = convC;
        row.conversions_value = convV;
      }
      const cost = Number(row.cost ?? 0);
      row.cpa = row.conversions > 0 ? cost / row.conversions : 0;
      row.roas = cost > 0 && row.conversions_value > 0 ? row.conversions_value / cost : null;
    },
    [
      adDetailByEntityId,
      hasGoogleConversionFilter,
      hasMetaConversionFilter,
      filterConversionIds,
      filterConversionTypes,
      normalizeGoogleConversionActionId,
      metaAllowedActionTypes,
      metaCanonicalFromActionType,
    ]
  );

  const adsBaseData = useMemo(() => {
    if (!tableDataAds) return [];
    const googleRaw = (tableDataAds?.google?.ad_metrics || []).map((m) => ({
      ...calculateDerivedMetrics(normalizeMetrics(m, 'ad')),
      platform: 'google',
      platformLogo: <GoogleAdsLogo />,
      originalCurrency: tableDataAds?.google?.account_currency || 'USD',
    }));
    const metaRaw = (tableDataAds?.meta?.ad_metrics || []).map((m) => ({
      ...calculateDerivedMetrics(normalizeMetrics(m, 'ad')),
      platform: 'meta',
      platformLogo: <MetaAdsLogo />,
      originalCurrency: tableDataAds?.meta?.account_currency || 'USD',
    }));
    let all = [...googleRaw, ...metaRaw].filter((m) => (m.impressions || 0) > 0);
    if (hasConversionFilter) {
      all.forEach((row) => applyConversionFilterToAdRow(row));
    }
    return all;
  }, [tableDataAds, hasConversionFilter, applyConversionFilterToAdRow]);

  const adsFlatData = useMemo(() => {
    const sortByColumn = (a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      if (aValue === null || aValue === undefined) aValue = 0;
      if (bValue === null || bValue === undefined) bValue = 0;
      aValue = typeof aValue === 'number' ? aValue : parseFloat(aValue) || 0;
      bValue = typeof bValue === 'number' ? bValue : parseFloat(bValue) || 0;
      if (['cost', 'cpa', 'cpc', 'cpm'].includes(sortColumn)) {
        const toCur = displayCurrency || 'USD';
        aValue = convertToDisplayCurrency(aValue, a.originalCurrency || 'USD', toCur, rates);
        bValue = convertToDisplayCurrency(bValue, b.originalCurrency || 'USD', toCur, rates);
      }
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    };
    let all = [...adsBaseData];
    if (campaignIdsSet.size > 0) {
      all = all.filter((a) => campaignIdsSet.has(String(a.campaignId ?? '')));
    }
    return [...all].sort(sortByColumn);
  }, [adsBaseData, campaignIdsSet, sortColumn, sortDirection, displayCurrency, rates]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const buildAndDownloadCSV = (fullData) => {
    if (!fullData?.tableData) return;
    const rows = [];
    const targetCurrency = fullData.display_currency || displayCurrency || 'USD';
    const exportRates = fullData.exchange_rates ? { USD: 1, ...fullData.exchange_rates } : rates;
    const convertForExport = (value, fromCurrency) =>
      convertToDisplayCurrency(value, fromCurrency || targetCurrency, targetCurrency, exportRates);

    const googleCampaignCurrency =
      fullData.tableData?.google?.account_currency || fullData.tableData?.display_currency || targetCurrency;
    const metaCampaignCurrency =
      fullData.tableData?.meta?.account_currency || fullData.tableData?.display_currency || targetCurrency;
    const googleAdGroupCurrency =
      fullData.tableDataAdGroups?.google?.account_currency || googleCampaignCurrency;
    const metaAdGroupCurrency =
      fullData.tableDataAdGroups?.meta?.account_currency || metaCampaignCurrency;
    const googleAdCurrency =
      fullData.tableDataAds?.google?.account_currency || googleCampaignCurrency;
    const metaAdCurrency =
      fullData.tableDataAds?.meta?.account_currency || metaCampaignCurrency;

    const addRow = (nivel, plataforma, campaña, grupoAnuncios, entidad, item) => {
      const currency = item.originalCurrency || targetCurrency;
      const convertMoney = (value) => convertForExport(value ?? 0, currency);
      const gasto = convertMoney(item.cost ?? 0);
      const conv = item.conversions ?? 0;
      const cpa = convertMoney(item.cpa ?? 0);
      const roas = coerceToNumber(item.roas);
      const clicks = item.clicks ?? 0;
      const cpc = convertMoney(item.cpc ?? 0);
      const ctr = item.ctr ?? 0;
      const impr = item.impressions ?? 0;
      const cpm = convertMoney(item.cpm ?? 0);
      rows.push([nivel, plataforma, campaña, grupoAnuncios, entidad, gasto, conv, cpa, roas != null ? roas : '', clicks, cpc, ctr, impr, cpm]);
    };

    const googleCampaigns = (fullData.tableData?.google?.campaign_metrics || []).map((m) => ({
      ...calculateDerivedMetrics(normalizeMetrics(m, 'campaign')),
      platform: 'Google Ads',
      originalCurrency: googleCampaignCurrency
    }));
    const metaCampaigns = (fullData.tableData?.meta?.campaign_metrics || []).map((m) => ({
      ...calculateDerivedMetrics(normalizeMetrics(m, 'campaign')),
      platform: 'Meta Ads',
      originalCurrency: metaCampaignCurrency
    }));
    [...googleCampaigns, ...metaCampaigns]
      .filter((m) => (m.impressions || 0) > 0)
      .forEach((c) => addRow('Campaña', c.platform, c.name, '', c.name, c));

    if (fullData.tableDataAdGroups) {
      const googleGroups = (fullData.tableDataAdGroups?.google?.ad_group_metrics || []).map((m) => ({
        ...calculateDerivedMetrics(normalizeMetrics(m, 'ad_group')),
        platform: 'Google Ads',
        originalCurrency: googleAdGroupCurrency
      }));
      const metaGroups = (fullData.tableDataAdGroups?.meta?.adset_metrics || []).map((m) => ({
        ...calculateDerivedMetrics(normalizeMetrics(m, 'ad_group')),
        platform: 'Meta Ads',
        originalCurrency: metaAdGroupCurrency
      }));
      [...googleGroups, ...metaGroups]
        .filter((m) => (m.impressions || 0) > 0)
        .forEach((g) => addRow('Grupo de anuncios', g.platform, g.campaignName || '', g.name, g.name, g));
    }

    if (fullData.tableDataAds) {
      const googleAds = (fullData.tableDataAds?.google?.ad_metrics || []).map((m) => ({
        ...calculateDerivedMetrics(normalizeMetrics(m, 'ad')),
        platform: 'Google Ads',
        originalCurrency: googleAdCurrency
      }));
      const metaAds = (fullData.tableDataAds?.meta?.ad_metrics || []).map((m) => ({
        ...calculateDerivedMetrics(normalizeMetrics(m, 'ad')),
        platform: 'Meta Ads',
        originalCurrency: metaAdCurrency
      }));
      [...googleAds, ...metaAds]
        .filter((m) => (m.impressions || 0) > 0)
        .forEach((a) => addRow('Anuncio', a.platform, a.campaignName || '', a.adGroupName || '', a.name, a));
    }

    const header = ['Nivel', 'Plataforma', 'Campaña', 'Grupo de anuncios', 'Entidad', 'Gasto', 'Conversiones', 'CPA', 'ROAS', 'Clics', 'CPC', 'CTR', 'Impresiones', 'CPM'];
    const csvContent = [header.map(escapeCSV).join(','), ...rows.map((r) => r.map(escapeCSV).join(','))].join('\r\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resultados-campanas-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = async () => {
    if (!getFullDataForExport || exportingCSV) return;
    setExportingCSV(true);
    try {
      const fullData = await getFullDataForExport();
      buildAndDownloadCSV(fullData);
    } finally {
      setExportingCSV(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'ad_groups' && !tableDataAdGroups && onRequestAdGroups) onRequestAdGroups();
    if (tab === 'ads' && !tableDataAds && onRequestAds) onRequestAds();
  };

  const tabTitles = {
    campaigns: 'Resultados por campaña',
    ad_groups: 'Resultados por grupo de anuncios',
    ads: 'Resultados por anuncio'
  };

  const currentRows = activeTab === 'campaigns' ? campaignsData : (activeTab === 'ad_groups' ? adGroupsFlatData : adsFlatData);
  const isLoadingTab = (activeTab === 'ad_groups' && loadingAdGroups && !tableDataAdGroups) || (activeTab === 'ads' && loadingAds && !tableDataAds);

  // Mobile Campaign Card Component
  const MobileCampaignCard = ({ item, isChecked, onToggleCheck, activeTab: tabType }) => {
    const allMetrics = [
      { label: 'Gasto', value: formatCurrency(item.cost || 0, item.originalCurrency), emphasized: true },
      { label: 'Conv.', value: (item.conversions || 0).toFixed(0), emphasized: true },
      { label: 'ROAS', value: item.roas !== null ? item.roas.toFixed(2) : '0.00', emphasized: false },
      { label: 'CPA', value: formatCurrency(item.cpa || 0, item.originalCurrency), emphasized: false },
      { label: 'Clicks', value: (item.clicks || 0).toLocaleString(), emphasized: false },
      { label: 'CPC', value: formatCurrency(item.cpc || 0, item.originalCurrency), emphasized: false },
      { label: 'CTR', value: `${(item.ctr || 0).toFixed(2)}%`, emphasized: false },
      { label: 'Impresiones', value: (item.impressions || 0).toLocaleString(), emphasized: false },
      { label: 'CPM', value: formatCurrency(item.cpm || 0, item.originalCurrency), emphasized: false }
    ];

    return (
      <div className="bg-white/10 border border-white/20 rounded-lg p-3 mb-3 backdrop-blur-sm">
        {/* Header with checkbox + platform + name */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {tabType === 'campaigns' && (
              <input
                type="checkbox"
                checked={isChecked}
                onChange={onToggleCheck}
                className="w-5 h-5 accent-blue-500 flex-shrink-0"
              />
            )}
            <div className="flex-shrink-0">{item.platformLogo}</div>
            <div className="min-w-0 flex-1">
              <h3 className="text-white font-medium text-sm truncate">{item.name}</h3>
              {item.campaignName && (
                <p className="text-white/60 text-xs truncate">{item.campaignName}</p>
              )}
            </div>
          </div>
        </div>

        {/* All metrics in compact 3-column grid */}
        <div className="grid grid-cols-3 gap-1.5">
          {allMetrics.map(m => (
            <div key={m.label} className={`p-1.5 rounded-lg ${m.emphasized ? 'bg-white/15' : 'bg-white/5'}`}>
              <p className="text-white/70 text-xs mb-0.5 line-clamp-1">{m.label}</p>
              <p className={`text-white font-semibold truncate ${m.emphasized ? 'text-sm' : 'text-xs'}`}>{m.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (parentLoading) {
    return <CampaignsTableSkeleton />;
  }

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:bg-gradient-to-br hover:from-slate-700/90 hover:via-slate-800/90 hover:to-indigo-800/90 hover:border-white/30 transition-all duration-500 ease-out mt-12 mx-0">
      <div className="relative z-10">
        {isMobile ? (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {[
              { key: 'campaigns', label: 'Campañas' },
              { key: 'ad_groups', label: 'Grupos' },
              { key: 'ads', label: 'Anuncios' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap min-h-[44px] ${
                  activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-white/10 text-white/70'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : (
          <div
            role="tablist"
            aria-label="Nivel de resultados"
            className="flex gap-1 mb-4 p-1 rounded-lg bg-white/5 border border-white/10"
          >
            {[
              { id: 'campaigns', label: 'Campañas' },
              { id: 'ad_groups', label: 'Grupos de anuncios' },
              { id: 'ads', label: 'Anuncios' }
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={activeTab === id}
                tabIndex={activeTab === id ? 0 : -1}
                onClick={() => handleTabChange(id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === id
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <div className={`flex ${isMobile ? 'flex-col gap-3' : 'justify-between items-center'} mb-6`}>
          <div className="text-left">
            <div className={`flex items-center gap-3 ${isMobile ? 'flex-wrap' : 'flex-wrap'}`}>
              <h3 className="text-lg font-bold text-white mb-1">{tabTitles[activeTab]}</h3>
              {activeTab === 'campaigns' && selectedCampaignIdsForFilter.size > 0 && onApplyFilter && (
                <button
                  type="button"
                  onClick={() => onApplyFilter(Array.from(selectedCampaignIdsForFilter))}
                  className={`rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors ${
                    isMobile ? 'px-3 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'
                  }`}
                >
                  Aplicar filtro
                </button>
              )}
              {activeTab === 'campaigns' && hasCampaignFilter && onClearFilter && (
                <button
                  type="button"
                  onClick={onClearFilter}
                  className={`rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors border border-white/20 ${
                    isMobile ? 'px-3 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'
                  }`}
                >
                  Borrar filtros
                </button>
              )}
            </div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
          </div>
          {!isMobile && (
            <button
              type="button"
              onClick={handleDownloadCSV}
              disabled={exportingCSV || !campaignsData.length || !getFullDataForExport}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
            >
              <Download className="w-4 h-4" />
              {exportingCSV ? 'Preparando…' : 'Descargar'}
            </button>
          )}
        </div>

        {isMobile ? (
          <>
            {/* Mobile View - Cards Layout */}
            {/* Controls: Sort + Filter */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setShowSortSheet(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg text-white/80 min-h-[44px]"
              >
                <ArrowUpDown className="w-4 h-4" />
                <span className="text-sm">Ordenar</span>
              </button>
              {activeTab === 'campaigns' && selectedCampaignIdsForFilter.size > 0 && (
                <button
                  onClick={() => onApplyFilter && onApplyFilter(Array.from(selectedCampaignIdsForFilter))}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg min-h-[44px] text-sm font-medium"
                >
                  Filtrar ({selectedCampaignIdsForFilter.size})
                </button>
              )}
            </div>

            {/* Cards */}
            {isLoadingTab ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white/10 rounded-lg p-4 h-48 animate-pulse" />
                ))}
              </div>
            ) : currentRows.length > 0 ? (
              <div className="space-y-3">
                {currentRows.map((item, idx) => {
                  const campaignId = activeTab === 'campaigns' ? String(item.entityId ?? '') : '';
                  const isChecked = activeTab === 'campaigns' && campaignId && selectedCampaignIdsForFilter.has(campaignId);
                  return (
                    <MobileCampaignCard
                      key={`${activeTab}-${item.platform}-${item.entityId}-${idx}`}
                      item={item}
                      isChecked={isChecked}
                      onToggleCheck={() => {
                        if (activeTab !== 'campaigns' || !campaignId) return;
                        setSelectedCampaignIdsForFilter(prev => {
                          const next = new Set(prev);
                          if (next.has(campaignId)) next.delete(campaignId);
                          else next.add(campaignId);
                          return next;
                        });
                      }}
                      activeTab={activeTab}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-white/70">
                <p>{activeTab === 'campaigns' && 'No hay campañas con impresiones'}</p>
                <p>{activeTab === 'ad_groups' && 'No hay grupos de anuncios con impresiones'}</p>
                <p>{activeTab === 'ads' && 'No hay anuncios con impresiones'}</p>
              </div>
            )}

            {/* Sort Bottom Sheet */}
            <MobileBottomSheet
              isOpen={showSortSheet}
              onClose={() => setShowSortSheet(false)}
              title="Ordenar por"
              darkMode
            >
              <div className="space-y-2">
                {[
                  { key: 'cost', label: 'Gasto' },
                  { key: 'conversions', label: 'Conversiones' },
                  { key: 'roas', label: 'ROAS' },
                  { key: 'cpa', label: 'CPA' },
                  { key: 'clicks', label: 'Clicks' },
                  { key: 'cpc', label: 'CPC' },
                  { key: 'ctr', label: 'CTR' },
                  { key: 'impressions', label: 'Impresiones' },
                  { key: 'cpm', label: 'CPM' }
                ].map(option => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSort(option.key);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg min-h-[44px] ${
                      sortColumn === option.key ? 'bg-blue-600 text-white' : 'bg-white/10 text-white/80'
                    }`}
                  >
                    {option.label} {sortColumn === option.key && (sortDirection === 'asc' ? '↑' : '↓')}
                  </button>
                ))}
              </div>
            </MobileBottomSheet>
          </>
        ) : (
          <>
            {/* Desktop View - Table Layout */}
            {isLoadingTab ? (
              <div className="overflow-x-auto rounded-lg">
                <table className="w-full min-w-full md:min-w-[800px]">
                  <thead>
                    <tr className="border-b border-white/30">
                      <th className="text-center py-3 px-4 w-10"><div className="h-4 w-4 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-16 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-left py-3 px-4"><div className="h-4 w-24 bg-white/20 rounded animate-pulse" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-12 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-10 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-12 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-10 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-10 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-10 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-10 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-10 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-12 bg-white/20 rounded animate-pulse mx-auto" /></th>
                      <th className="text-center py-3 px-4"><div className="h-4 w-10 bg-white/20 rounded animate-pulse mx-auto" /></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {[...Array(4)].map((_, rowIdx) => (
                      <tr key={rowIdx} className="hover:bg-white/5">
                        {[...Array(12)].map((_, colIdx) => (
                          <td key={colIdx} className={`py-4 px-4 ${colIdx === 2 ? 'text-left' : 'text-center'}`}>
                            <div className={`h-4 bg-white/20 rounded animate-pulse ${colIdx === 2 ? 'w-40' : 'w-12 mx-auto'}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : currentRows.length > 0 ? (
              <div className="overflow-x-auto rounded-lg">
                <table className="w-full min-w-full md:min-w-[800px]">
              <thead>
                <tr className="border-b border-white/30">
                  <th className="text-center py-3 px-4 font-medium text-white w-10">
                    {activeTab === 'campaigns' ? (
                      <span className="sr-only">Seleccionar</span>
                    ) : null}
                  </th>
                  <th className="text-center py-3 px-4 font-medium text-white">Plataforma</th>
                  <th className="text-left py-3 px-4 font-medium text-white">Entidad</th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('cost')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Gasto
                      {sortColumn === 'cost' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('conversions')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Conv.
                      {sortColumn === 'conversions' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('cpa')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      CPA
                      {sortColumn === 'cpa' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('roas')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      ROAS
                      {sortColumn === 'roas' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('clicks')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Clics
                      {sortColumn === 'clicks' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('cpc')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      CPC
                      {sortColumn === 'cpc' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('ctr')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      CTR
                      {sortColumn === 'ctr' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('impressions')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Impr.
                      {sortColumn === 'impressions' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-medium text-white cursor-pointer hover:bg-white/10 transition-colors select-none"
                    onClick={() => handleSort('cpm')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      CPM
                      {sortColumn === 'cpm' && (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/20">
                {currentRows.map((item, idx) => {
                  const campaignId = activeTab === 'campaigns' ? String(item.entityId ?? '') : '';
                  const isChecked = activeTab === 'campaigns' && campaignId && selectedCampaignIdsForFilter.has(campaignId);
                  const toggleCampaignSelection = () => {
                    if (activeTab !== 'campaigns' || !campaignId) return;
                    setSelectedCampaignIdsForFilter((prev) => {
                      const next = new Set(prev);
                      if (next.has(campaignId)) next.delete(campaignId);
                      else next.add(campaignId);
                      return next;
                    });
                  };
                  return (
                  <tr key={`${activeTab}-${item.platform}-${item.entityId}-${idx}`} className="hover:bg-white/10 transition-colors">
                    <td className="py-4 px-2 w-10">
                      {activeTab === 'campaigns' && campaignId ? (
                        <label className="flex items-center justify-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={toggleCampaignSelection}
                            className="rounded border-white/40 bg-white/5 text-blue-500 focus:ring-blue-500"
                          />
                        </label>
                      ) : null}
                    </td>
                    <td className="py-4 px-4 font-medium text-white text-center">
                      {item.platformLogo}
                    </td>
                    <td className="py-4 px-4 text-white">
                      <div className="max-w-xs">
                        <div className="truncate font-medium" title={item.name}>
                          {item.name}
                        </div>
                        {(activeTab === 'ad_groups' && item.campaignName) || (activeTab === 'ads' && (item.adGroupName || item.campaignName)) ? (
                          <div className="text-xs text-white/60 truncate mt-0.5">
                            {activeTab === 'ad_groups' ? item.campaignName : (item.adGroupName || item.campaignName)}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-4 px-4 font-semibold text-white text-center">
                      {formatCurrency(item.cost, item.originalCurrency || displayCurrency)}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {item.conversions.toFixed(1).replace('.0', '')}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {item.cpa > 0 ? formatCurrency(item.cpa, item.originalCurrency || displayCurrency) : formatCurrency(0, item.originalCurrency || displayCurrency)}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {coerceToNumber(item.roas) !== null ? coerceToNumber(item.roas).toFixed(2) : '0.00'}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {item.clicks.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {item.cpc > 0 ? formatCurrency(item.cpc, item.originalCurrency || displayCurrency) : 'N/A'}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {item.ctr > 0 ? `${item.ctr}%` : 'N/A'}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {item.impressions.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-white/90 text-center">
                      {item.cpm > 0 ? formatCurrency(item.cpm, item.originalCurrency || displayCurrency) : 'N/A'}
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-white/80 mb-2">
                    {activeTab === 'campaigns' && 'No hay campañas con impresiones'}
                    {activeTab === 'ad_groups' && 'No hay grupos de anuncios con impresiones'}
                    {activeTab === 'ads' && 'No hay anuncios con impresiones'}
                  </p>
                  <p className="text-sm text-white/60">
                    {activeTab === 'campaigns' && "No se encontraron campañas con impresiones > 0 para el período seleccionado"}
                    {activeTab === 'ad_groups' && "No se encontraron grupos de anuncios con impresiones > 0 para el período seleccionado"}
                    {activeTab === 'ads' && "No se encontraron anuncios con impresiones > 0 para el período seleccionado"}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Analytics20 = () => {
  const [selectedPeriod, _setSelectedPeriod] = useState('ESTE_MES');
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const [customStartDate, _setCustomStartDate] = useState('');
  const [customEndDate, _setCustomEndDate] = useState('');
  // Estado para comparación (simple toggle)
  const [isCompareEnabled, _setIsCompareEnabled] = useState(false);
  // Estados para comparación personalizada
  const [compareType, _setCompareType] = useState('previous'); // 'previous' o 'custom'
  const [customCompareStartDate, _setCustomCompareStartDate] = useState('');
  const [customCompareEndDate, _setCustomCompareEndDate] = useState('');
  // Iniciar en true para evitar el "flash" de estado vacío antes de la primera carga
  const [loading, setLoading] = useState(true);
  const [combinedData, setCombinedData] = useState(null); // Para cards y métricas (siempre campaign)
  const [tableData, setTableData] = useState(null); // Para la tabla (solo campañas)
  // Detalle de conversiones por campaña, por plataforma (viene de campaign_conversions_detail del backend)
  const [campaignConversionsDetail, setCampaignConversionsDetail] = useState({ google: [], meta: [] });
  const [tableDataAdGroups, setTableDataAdGroups] = useState(null); // Grupos de anuncios (lazy)
  const [tableDataAds, setTableDataAds] = useState(null); // Anuncios (lazy)
  const [loadingAdGroups, setLoadingAdGroups] = useState(false);
  const [loadingAds, setLoadingAds] = useState(false);
  // Serie diaria para gráfico de evolución (desde respuesta granularity=daily)
  const [dailyData, setDailyData] = useState(null);
  // Dos métricas para el gráfico de evolución (dos dropdowns) — mismas que las cards
  const [selectedMetric1, setSelectedMetric1] = useState('Costo');
  const [selectedMetric2, setSelectedMetric2] = useState('Clicks');
  // Estados para datos de comparación
  const [comparisonData, setComparisonData] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  // Ref para ignorar respuestas de fetches obsoletos (evitar mostrar datos de 7 días mientras carga mes pasado)
  const fetchIdRef = useRef(0);
  const { auth } = useAuth();
  const { active_brand_id } = auth?.user || {};
  const { startDate: filterStartDate, endDate: filterEndDate, selectedCampaignIds: filterCampaignIds, setSelectedCampaignIds: setFilterCampaignIds, selectedConversions: filterSelectedConversions, setCampaignsFromTable } = useAnalyticsFilter();
  const { isMobile } = useResponsive();

  const filterConversionIds = useMemo(
    () => (filterSelectedConversions || []).filter((c) => c.platform === 'google').map((c) => c.id),
    [filterSelectedConversions]
  );
  const filterConversionTypes = useMemo(
    () => (filterSelectedConversions || []).filter((c) => c.platform === 'meta').map((c) => c.id),
    [filterSelectedConversions]
  );

  // Flags y helpers de filtro de conversiones (para tableDataForView / campañas)
  const hasConversionFilter = (filterConversionIds?.length || filterConversionTypes?.length) > 0;
  const hasGoogleConversionFilter = filterConversionIds?.length > 0;
  const hasMetaConversionFilter = filterConversionTypes?.length > 0;
  const normalizeGoogleConversionActionId = useCallback((value) => {
    if (value == null) return null;
    const s = String(value).trim();
    if (!s) return null;
    if (s.includes('/')) return s.split('/').pop();
    return s;
  }, []);
  const metaAllowedActionTypes = useCallback((conversionTypes) => {
    if (!conversionTypes || conversionTypes.length === 0) return null;
    const allowed = new Set();
    conversionTypes.forEach((c) => {
      const s = String(c || '').trim();
      if (!s) return;
      const underscoreIndex = s.indexOf('_');
      const prefix = underscoreIndex >= 0 ? s.slice(0, underscoreIndex) : '';
      if (underscoreIndex >= 0 && /^\d+$/.test(prefix)) {
        allowed.add(s.slice(underscoreIndex + 1).toLowerCase());
      } else {
        allowed.add(s.toLowerCase());
      }
    });
    return allowed.size ? allowed : null;
  }, []);
  const metaCanonicalFromActionType = useCallback((actionType) => {
    let at = (actionType || '').trim().toLowerCase();
    if (!at) return '';
    if (!at.includes('.')) return at;
    const lastSegment = at.split('.').pop();
    if (lastSegment.includes('_')) return lastSegment.split('_').pop();
    return lastSegment;
  }, []);

  // Estado para configuración de métricas en cada card (con persistencia en localStorage)
  // Cada posición del array representa qué métrica mostrar en esa card
  const [cardMetrics, setCardMetrics] = useState(() => loadCardMetricsFromStorage());

  // Guardar configuración de cards en localStorage cuando cambien
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(CARD_METRICS_STORAGE_KEY, JSON.stringify(cardMetrics));
      window.localStorage.setItem(CARD_METRICS_VERSION_KEY, String(CARD_METRICS_STORAGE_VERSION));
    } catch (error) {
      console.warn('No se pudo persistir el orden de métricas', error);
    }
  }, [cardMetrics]);

  // Sincronizar con el filtro de campañas: las opciones del filtro son las mismas que en la tabla "Resultados por campaña"
  useEffect(() => {
    if (!tableData) {
      setCampaignsFromTable([]);
      return;
    }
    const list = [];
    const googleRaw = tableData?.google?.campaign_metrics || [];
    const metaRaw = tableData?.meta?.campaign_metrics || [];
    googleRaw.filter((m) => (m.impressions || 0) > 0).forEach((m) => {
      list.push({
        id: String(m.campaign_id ?? ''),
        name: m.campaign_name || m.name || 'Sin nombre',
        platform: 'google',
      });
    });
    metaRaw.filter((m) => (m.impressions || 0) > 0).forEach((m) => {
      list.push({
        id: String(m.campaign_id ?? ''),
        name: m.campaign_name || m.name || 'Sin nombre',
        platform: 'meta',
      });
    });
    setCampaignsFromTable(list);
  }, [tableData, setCampaignsFromTable]);

  // Filtrar datos por plataforma y campañas en frontend (sin refetch)
  const campaignIdsSet = useMemo(
    () => (filterCampaignIds?.length ? new Set(filterCampaignIds.map(String)) : null),
    [filterCampaignIds]
  );

  const filteredTableData = useMemo(() => {
    if (!tableData) return null;
    const googleRaw = tableData.google?.campaign_metrics || [];
    const metaRaw = tableData.meta?.campaign_metrics || [];
    const filterByCampaigns = (rows) =>
      campaignIdsSet ? rows.filter((r) => campaignIdsSet.has(String(r.campaign_id ?? ''))) : rows;
    const filteredGoogle = filterByCampaigns(googleRaw);
    const filteredMeta = filterByCampaigns(metaRaw);
    return {
      ...tableData,
      google: tableData.google ? { ...tableData.google, campaign_metrics: filteredGoogle } : tableData.google,
      meta: tableData.meta ? { ...tableData.meta, campaign_metrics: filteredMeta } : tableData.meta
    };
  }, [tableData, campaignIdsSet]);

  const filteredCombinedView = useMemo(() => {
    if (!filteredTableData || !combinedData) return null;
    const displayCurrencyVal = combinedData.display_currency || 'USD';
    const rates = { USD: 1, ...(combinedData.exchange_rates || {}) };
    const google_currency = filteredTableData?.google?.account_currency || 'USD';
    const meta_currency = filteredTableData?.meta?.account_currency || 'USD';
    const googleRaw = filteredTableData.google?.campaign_metrics || [];
    const metaRaw = filteredTableData.meta?.campaign_metrics || [];
    const googleAgg = aggregateCampaignRows(googleRaw);
    const metaAgg = aggregateCampaignRows(metaRaw);
    // Convertir a display_currency antes de sumar (tasas desde DB)
    const googleInv = convertToDisplayCurrency(googleAgg.investment, google_currency, displayCurrencyVal, rates);
    const googleCv = convertToDisplayCurrency(googleAgg.conversions_value, google_currency, displayCurrencyVal, rates);
    const metaInv = convertToDisplayCurrency(metaAgg.investment, meta_currency, displayCurrencyVal, rates);
    const metaCv = convertToDisplayCurrency(metaAgg.conversions_value, meta_currency, displayCurrencyVal, rates);
    const totalCost = googleInv + metaInv;
    const totalCv = googleCv + metaCv;
    return {
      combined: {
        investment: totalCost,
        cost: totalCost,
        currency: displayCurrencyVal,
        activeCampaigns: googleAgg.activeCampaigns + metaAgg.activeCampaigns,
        clicks: googleAgg.clicks + metaAgg.clicks,
        impressions: googleAgg.impressions + metaAgg.impressions,
        conversions: googleAgg.conversions + metaAgg.conversions,
        conversions_value: totalCv,
        roas: totalCost > 0 && totalCv > 0 ? totalCv / totalCost : null
      },
      google_normalized: {
        investment: googleInv,
        cost: googleInv,
        currency: displayCurrencyVal,
        activeCampaigns: googleAgg.activeCampaigns,
        clicks: googleAgg.clicks,
        impressions: googleAgg.impressions,
        conversions: googleAgg.conversions,
        conversions_value: googleCv,
        roas: googleInv > 0 && googleCv > 0 ? googleCv / googleInv : (googleAgg.roas ?? null),
        platform: 'google'
      },
      meta_normalized: {
        investment: metaInv,
        cost: metaInv,
        currency: displayCurrencyVal,
        activeCampaigns: metaAgg.activeCampaigns,
        clicks: metaAgg.clicks,
        impressions: metaAgg.impressions,
        conversions: metaAgg.conversions,
        conversions_value: metaCv,
        roas: metaInv > 0 && metaCv > 0 ? metaCv / metaInv : (metaAgg.roas ?? null),
        platform: 'meta'
      }
    };
  }, [filteredTableData, combinedData]);

  const basePeriods = useMemo(
    () => [
      { key: 'TODAY', label: 'Hoy', days: 0, granularity: 'daily' },
      { key: 'YESTERDAY', label: 'Ayer', days: 1, granularity: 'daily' },
      { key: '7D', label: 'Últimos 7 días', days: 7, granularity: 'daily' },
      { key: '14D', label: 'Últimos 14 días', days: 14, granularity: 'daily' },
      { key: '30D', label: 'Últimos 30 días', days: 30, granularity: 'daily' },
      { key: 'ESTE_MES', label: 'Este mes', days: 'current_month', granularity: 'daily' },
      { key: 'MES_PASADO', label: 'El mes pasado', days: 'last_month', granularity: 'daily' }
    ],
    []
  );

  const customPeriodLabel = useMemo(() => {
    if (!(customStartDate && customEndDate)) {
      return 'Personalizado';
    }
    const startDate = new Date(customStartDate);
    const endDate = new Date(customEndDate);
    if (!isValid(startDate) || !isValid(endDate)) {
      return 'Personalizado';
    }
    return `${formatDate(startDate, 'full-year-short')} - ${formatDate(endDate, 'full-year-short')}`;
  }, [customStartDate, customEndDate]);

  const customPeriodDays = useMemo(() => {
    if (!(customStartDate && customEndDate)) {
      return null;
    }
    const startDate = new Date(customStartDate);
    const endDate = new Date(customEndDate);
    if (!isValid(startDate) || !isValid(endDate)) {
      return null;
    }
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }, [customStartDate, customEndDate]);

  const periods = useMemo(() => {
    const customFormattedLabel =
      customPeriodLabel && customPeriodLabel !== 'Personalizado' ? customPeriodLabel : '';
    return [
      ...basePeriods,
      {
        key: 'CUSTOM',
        label: 'Personalizado',
        days: customPeriodDays,
        granularity: 'daily',
        formattedLabel: customFormattedLabel
      }
    ];
  }, [basePeriods, customPeriodDays, customPeriodLabel]);

  // Verificar conexiones
  const isGoogleConnected = auth?.user?.google_ads_connected && auth?.user?.google_ads_customer_id;
  const isMetaConnected = auth?.user?.meta_connected && auth?.user?.meta_ad_account_id;
  const hasAnyConnection = isGoogleConnected || isMetaConnected;

  // Acciones para conectar cuentas
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();


  // Función simple para calcular el período anterior
  const calculatePreviousPeriod = useCallback((periodKey, days, customStartDate, customEndDate) => {
    const currentRange = calculateDateRange(periodKey, days, customStartDate, customEndDate);
    if (!currentRange || !currentRange.startDate || !currentRange.endDate) return null;
    
    // Si hay fechas de comparación personalizadas, usarlas
    if (compareType === 'custom' && customCompareStartDate && customCompareEndDate) {
      return {
        start: customCompareStartDate,
        end: customCompareEndDate
      };
    }
    
    // Normalizar fechas
    const start = new Date(
      currentRange.startDate.getFullYear(),
      currentRange.startDate.getMonth(),
      currentRange.startDate.getDate()
    );
    const end = new Date(
      currentRange.endDate.getFullYear(),
      currentRange.endDate.getMonth(),
      currentRange.endDate.getDate()
    );
    
    // Calcular diferencia en días (incluyendo ambos días)
    const diffTime = end - start;
    const diffDays = diffTime === 0 ? 1 : Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    // Período anterior termina 1 día antes del inicio del período actual
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    
    // Período anterior comienza diffDays días antes del final
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - diffDays + 1);
    
    return {
      start: formatDate(prevStart, 'full-year-day'),
      end: formatDate(prevEnd, 'full-year-day')
    };
  }, [compareType, customCompareStartDate, customCompareEndDate]);


  // Función simple para obtener datos de comparación
  const fetchComparisonData = useCallback(async (periodKey, days, rangeOverride) => {
    if (!isCompareEnabled) return null;
    
    try {
      let startStr;
      let endStr;

      if (compareType === 'custom') {
        if (rangeOverride && rangeOverride.startDateStr && rangeOverride.endDateStr) {
          startStr = rangeOverride.startDateStr;
          endStr = rangeOverride.endDateStr;
        } else if (customCompareStartDate && customCompareEndDate) {
          startStr = customCompareStartDate;
          endStr = customCompareEndDate;
        } else {
          return null;
        }
      } else {
        const previousPeriod = calculatePreviousPeriod(periodKey, days, filterStartDate, filterEndDate);
        if (!previousPeriod) return null;
        startStr = previousPeriod.start;
        endStr = previousPeriod.end;
      }

      const response = await analyticsApi.fetchCombinedAnalyticsSummary(
        startStr,
        endStr,
        active_brand_id,
        'total',
        'campaign',
        'all',
        null,
        null,
        null
      );
      return response;
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      return null;
    }
  }, [isCompareEnabled, compareType, calculatePreviousPeriod, filterStartDate, filterEndDate, customCompareStartDate, customCompareEndDate, active_brand_id]);

  // Fetch Combined Analytics: una sola llamada con granularity daily; se derivan totales y tabla desde response.data
  // fetchIdOp: si se pasa, solo actualizamos estado si sigue siendo el fetch vigente (evita race conditions)
  const fetchCombinedData = useCallback(async (periodKey, days, rangeOverride, compareRangeOverride, fetchIdOp) => {
    if (!hasAnyConnection) return;
    const isStale = () => fetchIdOp != null && fetchIdRef.current !== fetchIdOp;
    try {
      const startStr = filterStartDate || (rangeOverride?.startDateStr);
      const endStr = filterEndDate || (rangeOverride?.endDateStr);
      if (!startStr || !endStr) return;

      const [mainData, comparisonDataResult] = await Promise.allSettled([
        fetchCombinedAnalyticsSummaryNoCache(
          startStr,
          endStr,
          active_brand_id,
          'daily',
          'campaign',
          'all',
          filterCampaignIds?.length ? filterCampaignIds : null,
          null,
          null
        ),
        fetchComparisonData(periodKey, days, compareRangeOverride)
      ]);

      if (mainData.status === 'fulfilled' && mainData.value) {
        if (isStale()) return; // Ignorar respuesta obsoleta (otro fetch ya en curso o completado)
        const response = mainData.value;
        const displayCurrencyVal = response.display_currency || 'USD';
        setDisplayCurrency(displayCurrencyVal);

        if (response.granularity === 'daily' && Array.isArray(response.data)) {
          // Si el backend envió totales filtrados por conversión (summary_totals), usarlos para las cards
          let derivedCombinedData;
          if (response.summary_totals && (response.summary_totals.combined || response.summary_totals.google_normalized || response.summary_totals.meta_normalized)) {
            derivedCombinedData = {
              combined: response.summary_totals.combined || {},
              google_normalized: response.summary_totals.google_normalized || {},
              meta_normalized: response.summary_totals.meta_normalized || {},
              display_currency: displayCurrencyVal,
              exchange_rates: response.exchange_rates || { USD: 1 }
            };
          } else {
            // Derivar combinedData: sumar combined, google_normalized, meta_normalized por día
            const combinedSum = { investment: 0, cost: 0, clicks: 0, impressions: 0, conversions: 0, conversions_value: 0, activeCampaigns: 0 };
            const googleSum = { investment: 0, cost: 0, clicks: 0, impressions: 0, conversions: 0, conversions_value: 0, activeCampaigns: 0 };
            const metaSum = { investment: 0, cost: 0, clicks: 0, impressions: 0, conversions: 0, conversions_value: 0, activeCampaigns: 0 };
            for (const day of response.data) {
              const c = day.combined || {};
              const g = day.google_normalized || {};
              const m = day.meta_normalized || {};
              combinedSum.investment += Number(c.investment ?? c.cost ?? 0);
              combinedSum.cost += Number(c.cost ?? c.investment ?? 0);
              combinedSum.clicks += Number(c.clicks ?? 0);
              combinedSum.impressions += Number(c.impressions ?? 0);
              combinedSum.conversions += Number(c.conversions ?? 0);
              combinedSum.conversions_value += Number(c.conversions_value ?? 0);
              combinedSum.activeCampaigns += Number(c.activeCampaigns ?? 0);
              googleSum.investment += Number(g.investment ?? g.cost ?? 0);
              googleSum.cost += Number(g.cost ?? g.investment ?? 0);
              googleSum.clicks += Number(g.clicks ?? 0);
              googleSum.impressions += Number(g.impressions ?? 0);
              googleSum.conversions += Number(g.conversions ?? 0);
              googleSum.conversions_value += Number(g.conversions_value ?? 0);
              googleSum.activeCampaigns += Number(g.activeCampaigns ?? 0);
              metaSum.investment += Number(m.investment ?? m.cost ?? 0);
              metaSum.cost += Number(m.cost ?? m.investment ?? 0);
              metaSum.clicks += Number(m.clicks ?? 0);
              metaSum.impressions += Number(m.impressions ?? 0);
              metaSum.conversions += Number(m.conversions ?? 0);
              metaSum.conversions_value += Number(m.conversions_value ?? 0);
              metaSum.activeCampaigns += Number(m.activeCampaigns ?? 0);
            }
            const roasCombined = combinedSum.investment > 0 && combinedSum.conversions_value > 0 ? combinedSum.conversions_value / combinedSum.investment : null;
            const roasGoogle = googleSum.investment > 0 && googleSum.conversions_value > 0 ? googleSum.conversions_value / googleSum.investment : null;
            const roasMeta = metaSum.investment > 0 && metaSum.conversions_value > 0 ? metaSum.conversions_value / metaSum.investment : null;
            derivedCombinedData = {
              combined: {
                investment: combinedSum.investment,
                cost: combinedSum.cost,
                currency: displayCurrencyVal,
                activeCampaigns: combinedSum.activeCampaigns,
                clicks: combinedSum.clicks,
                impressions: combinedSum.impressions,
                conversions: combinedSum.conversions,
                conversions_value: combinedSum.conversions_value,
                roas: roasCombined
              },
              google_normalized: {
                investment: googleSum.investment,
                cost: googleSum.cost,
                currency: displayCurrencyVal,
                activeCampaigns: googleSum.activeCampaigns,
                clicks: googleSum.clicks,
                impressions: googleSum.impressions,
                conversions: googleSum.conversions,
                conversions_value: googleSum.conversions_value,
                roas: roasGoogle,
                platform: 'google'
              },
              meta_normalized: {
                investment: metaSum.investment,
                cost: metaSum.cost,
                currency: displayCurrencyVal,
                activeCampaigns: metaSum.activeCampaigns,
                clicks: metaSum.clicks,
                impressions: metaSum.impressions,
                conversions: metaSum.conversions,
                conversions_value: metaSum.conversions_value,
                roas: roasMeta,
                platform: 'meta'
              },
              display_currency: displayCurrencyVal,
              exchange_rates: response.exchange_rates || { USD: 1 }
            };
          }
          setCombinedData(derivedCombinedData);

          // Tabla campañas: agregar desde .campaign_metrics o agrupar desde .data
          const googleCampaignMetrics =
            response.google?.campaign_metrics != null && Array.isArray(response.google.campaign_metrics)
              ? response.google.campaign_metrics
              : aggregateByCampaign(response.google?.data || []);
          const metaCampaignMetrics =
            response.meta?.campaign_metrics != null && Array.isArray(response.meta.campaign_metrics)
              ? response.meta.campaign_metrics
              : aggregateByCampaign(response.meta?.data || []);
          const derivedTableData = {
            google: {
              campaign_metrics: googleCampaignMetrics,
              account_currency: response.google?.account_currency || displayCurrencyVal
            },
            meta: {
              campaign_metrics: metaCampaignMetrics,
              account_currency: response.meta?.account_currency || displayCurrencyVal
            },
            display_currency: displayCurrencyVal,
            exchange_rates: response.exchange_rates || { USD: 1 }
          };
          setTableData(derivedTableData);
          // Guardar detalle de conversiones por campaña (para recalcular CPA/ROAS en el frontend sin nuevas llamadas)
          setCampaignConversionsDetail({
            google: response.google?.campaign_conversions_detail || [],
            meta: response.meta?.campaign_conversions_detail || []
          });
          setDailyData(response.data);
        } else {
          setDailyData(null);
          setCombinedData(null);
          setTableData(null);
        }
      }

      if (comparisonDataResult.status === 'fulfilled' && comparisonDataResult.value && !isStale()) {
        setComparisonData(comparisonDataResult.value);
      }

    } catch (error) {
      if (!isStale()) console.error('Error fetching combined data:', error);
    }
  }, [hasAnyConnection, fetchComparisonData, active_brand_id, filterStartDate, filterEndDate, filterCampaignIds]);

  const onRequestAdGroups = useCallback(async () => {
    if (!hasAnyConnection || tableDataAdGroups !== null || loadingAdGroups || !filterStartDate || !filterEndDate) return;
    setLoadingAdGroups(true);
    try {
      const response = await analyticsApi.fetchCombinedAnalyticsSummary(
        filterStartDate,
        filterEndDate,
        active_brand_id,
        'total',
        'ad_group',
        'all',
        filterCampaignIds?.length ? filterCampaignIds : null,
        filterConversionIds.length ? filterConversionIds : null,
        filterConversionTypes.length ? filterConversionTypes : null
      );
      if (response) setTableDataAdGroups(response);
    } catch (error) {
      console.error('Error fetching ad groups:', error);
    } finally {
      setLoadingAdGroups(false);
    }
  }, [hasAnyConnection, tableDataAdGroups, loadingAdGroups, filterStartDate, filterEndDate, active_brand_id, filterCampaignIds, filterConversionIds, filterConversionTypes]);

  const onRequestAds = useCallback(async () => {
    if (!hasAnyConnection || tableDataAds !== null || loadingAds || !filterStartDate || !filterEndDate) return;
    setLoadingAds(true);
    try {
      const response = await analyticsApi.fetchCombinedAnalyticsSummary(
        filterStartDate,
        filterEndDate,
        active_brand_id,
        'total',
        'ad',
        'all',
        filterCampaignIds?.length ? filterCampaignIds : null,
        filterConversionIds.length ? filterConversionIds : null,
        filterConversionTypes.length ? filterConversionTypes : null
      );
      if (response) setTableDataAds(response);
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoadingAds(false);
    }
  }, [hasAnyConnection, tableDataAds, loadingAds, filterStartDate, filterEndDate, active_brand_id, filterCampaignIds, filterConversionIds, filterConversionTypes]);

  const getFullDataForExport = useCallback(async () => {
    const exportTableData = filteredTableData ?? tableData;
    if (!hasAnyConnection || !exportTableData || !filterStartDate || !filterEndDate) {
      return { tableData: null, tableDataAdGroups: null, tableDataAds: null };
    }
    const filteredCampaignIds = new Set();
    (exportTableData.google?.campaign_metrics || []).forEach((m) => filteredCampaignIds.add(String(m.campaign_id ?? '')));
    (exportTableData.meta?.campaign_metrics || []).forEach((m) => filteredCampaignIds.add(String(m.campaign_id ?? '')));

    const filterByCampaigns = (rows) => rows.filter((r) => filteredCampaignIds.has(String(r.campaign_id ?? '')));

    let adGroups = tableDataAdGroups;
    let ads = tableDataAds;
    if (!adGroups) {
      setLoadingAdGroups(true);
      try {
        adGroups = await analyticsApi.fetchCombinedAnalyticsSummary(
          filterStartDate,
          filterEndDate,
          active_brand_id,
          'total',
          'ad_group',
          'all',
          filterCampaignIds?.length ? filterCampaignIds : null,
          filterConversionIds.length ? filterConversionIds : null,
          filterConversionTypes.length ? filterConversionTypes : null
        );
        if (adGroups) setTableDataAdGroups(adGroups);
      } catch (error) {
        console.error('Error fetching ad groups for export:', error);
      } finally {
        setLoadingAdGroups(false);
      }
    }
    if (!ads) {
      setLoadingAds(true);
      try {
        ads = await analyticsApi.fetchCombinedAnalyticsSummary(
          filterStartDate,
          filterEndDate,
          active_brand_id,
          'total',
          'ad',
          'all',
          filterCampaignIds?.length ? filterCampaignIds : null,
          filterConversionIds.length ? filterConversionIds : null,
          filterConversionTypes.length ? filterConversionTypes : null
        );
        if (ads) setTableDataAds(ads);
      } catch (error) {
        console.error('Error fetching ads for export:', error);
      } finally {
        setLoadingAds(false);
      }
    }

    const filteredAdGroups =
      adGroups && exportTableData
        ? {
            ...adGroups,
            google: adGroups.google
              ? { ...adGroups.google, ad_group_metrics: filterByCampaigns(adGroups.google.ad_group_metrics || []) }
              : adGroups.google,
            meta: adGroups.meta
              ? { ...adGroups.meta, adset_metrics: filterByCampaigns(adGroups.meta.adset_metrics || []) }
              : adGroups.meta
          }
        : adGroups;
    const filteredAds =
      ads && exportTableData
        ? {
            ...ads,
            google: ads.google
              ? { ...ads.google, ad_metrics: filterByCampaigns(ads.google.ad_metrics || []) }
              : ads.google,
            meta: ads.meta
              ? { ...ads.meta, ad_metrics: filterByCampaigns(ads.meta.ad_metrics || []) }
              : ads.meta
          }
        : ads;

    return {
      tableData: exportTableData,
      tableDataAdGroups: filteredAdGroups || null,
      tableDataAds: filteredAds || null
    };
  }, [hasAnyConnection, filteredTableData, tableData, tableDataAdGroups, tableDataAds, filterStartDate, filterEndDate, active_brand_id, filterCampaignIds, filterConversionIds, filterConversionTypes]);

  // Precarga grupos de anuncios y anuncios en background una vez cargadas las campañas (no bloquea la vista inicial)
  useEffect(() => {
    if (!tableData || !filterStartDate || !filterEndDate || !hasAnyConnection) return;
    if (tableDataAdGroups === null && !loadingAdGroups) onRequestAdGroups();
    if (tableDataAds === null && !loadingAds) onRequestAds();
  }, [tableData, filterStartDate, filterEndDate, hasAnyConnection, tableDataAdGroups, tableDataAds, loadingAdGroups, loadingAds, onRequestAdGroups, onRequestAds]);

  // Una sola llamada al back: solo refetch cuando cambian fechas (o campañas). El filtro de conversiones se aplica en el front con campaign_conversions_detail.
  // Limpiamos datos y usamos fetchId para ignorar respuestas obsoletas: evita mostrar datos de 7 días mientras carga el mes pasado.
  // Grupos de anuncios y anuncios se resetean aquí para recalcularse con el nuevo período (en background, tras renderizar campañas).
  useEffect(() => {
    if (!hasAnyConnection || !filterStartDate || !filterEndDate) return;
    const period = periods.find(p => p.key === selectedPeriod);
    if (!period) return;

    const thisFetchId = ++fetchIdRef.current;
    setLoading(true);
    setCombinedData(null);
    setTableData(null);
    setDailyData(null);
    setTableDataAdGroups(null);
    setTableDataAds(null);

    fetchCombinedData(period.key, period.days, null, null, thisFetchId).finally(() => {
      if (thisFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStartDate, filterEndDate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isPeriodDropdownOpen && !event.target.closest('.period-dropdown-container')) {
        setIsPeriodDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPeriodDropdownOpen]);

  // Función para calcular el cambio porcentual
  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  // Función para formatear el cambio porcentual
  const formatPercentageChange = (change) => {
    if (change === null) return null;
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };


  // Función para obtener el color del cambio
  const getChangeColor = (change, isInverted = false) => {
    if (change === null) return 'text-gray-500';
    
    const isPositive = change > 0;
    const shouldBeGreen = isInverted ? !isPositive : isPositive;
    
    return shouldBeGreen ? 'text-green-600' : 'text-red-600';
  };

  // Función para obtener las fechas del período de comparación
  const getComparisonDates = () => {
    if (!selectedPeriod || !comparisonData || !isCompareEnabled) return null;
    
    const period = periods.find(p => p.key === selectedPeriod);
    if (!period) return null;
    
    const previousPeriod = calculatePreviousPeriod(selectedPeriod, period.days, filterStartDate, filterEndDate);
    if (!previousPeriod) return null;
    
    return {
      start: previousPeriod.start,
      end: previousPeriod.end
    };
  };

  const exchangeRatesForDisplay = { USD: 1, ...(combinedData?.exchange_rates || {}) };
  const formatCurrency = (value, currency = 'USD') => {
    if (typeof value !== 'number') return '0';
    let convertedValue = value;
    let targetCurrency = displayCurrency || 'USD';
    if (currency !== targetCurrency && exchangeRatesForDisplay[currency] != null && exchangeRatesForDisplay[targetCurrency] != null) {
      convertedValue = value * (exchangeRatesForDisplay[currency] / exchangeRatesForDisplay[targetCurrency]);
    } else if (currency !== targetCurrency) {
      targetCurrency = currency;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: targetCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedValue);
  };

  // Recalcular métricas por campaña y totales cuando hay filtro de conversiones, usando campaign_conversions_detail
  const { tableDataForView, combinedForView: dataForCards } = useMemo(() => {
    if (!tableData || !combinedData) {
      return {
        tableDataForView: filteredTableData ?? tableData,
        combinedForView: filteredCombinedView ?? combinedData,
      };
    }

    // Si no hay filtro de conversiones, usar vista filtrada solo por campañas
    if (!hasConversionFilter) {
      return {
        tableDataForView: filteredTableData ?? tableData,
        combinedForView: filteredCombinedView ?? combinedData,
      };
    }

    const baseTable = filteredTableData ?? tableData;
    const displayCurrencyVal = combinedData.display_currency || 'USD';
    const rates = { USD: 1, ...(combinedData.exchange_rates || {}) };

    // Clonar métricas de campaña para no mutar el estado base
    const googleBase = (baseTable.google?.campaign_metrics || []).map((r) => ({ ...r }));
    const metaBase = (baseTable.meta?.campaign_metrics || []).map((r) => ({ ...r }));

    // Mapas de detalle por campaña
    const googleDetailByCampaign = new Map();
    (campaignConversionsDetail.google || []).forEach((c) => {
      const cid = String(c.campaign_id ?? '');
      if (!cid) return;
      googleDetailByCampaign.set(cid, c.conversions_detail || []);
    });

    const metaDetailByCampaign = new Map();
    (campaignConversionsDetail.meta || []).forEach((c) => {
      const cid = String(c.campaign_id ?? '');
      if (!cid) return;
      metaDetailByCampaign.set(cid, c.conversions_detail || []);
    });

    // Google: aplicar filtro por conversion_ids si corresponde
    if (hasGoogleConversionFilter) {
      const allowedIds = new Set((filterConversionIds || []).map((id) => normalizeGoogleConversionActionId(id)).filter(Boolean));
      googleBase.forEach((row) => {
        const cid = String(row.campaign_id ?? '');
        if (!cid) return;
        const details = googleDetailByCampaign.get(cid);
        if (!details || details.length === 0) {
          // Sin detalle, mantener métricas originales
          return;
        }
        let convC = 0;
        let convV = 0;
        details.forEach((detail) => {
          const caId = normalizeGoogleConversionActionId(detail.conversion_action_id);
          if (caId && allowedIds.has(caId)) {
            convC += Number(detail.conversions ?? 0);
            convV += Number(detail.conversions_value ?? 0);
          }
        });
        // Si no hay conversiones seleccionadas para esta campaña, dejarla en 0
        row.conversions = convC;
        row.conversions_value = convV;
        const cost = Number(row.cost ?? 0);
        row.cost_per_conversion = convC > 0 ? cost / convC : 0;
      });
    }

    // Meta: aplicar filtro por conversion_types si corresponde
    if (hasMetaConversionFilter) {
      const allowedActionTypes = metaAllowedActionTypes(filterConversionTypes);
      if (allowedActionTypes && allowedActionTypes.size > 0) {
        const canonicalAllowed = new Set(
          Array.from(allowedActionTypes).filter((a) => !a.includes('.'))
        );

        metaBase.forEach((row) => {
          const cid = String(row.campaign_id ?? '');
          if (!cid) return;
          const details = metaDetailByCampaign.get(cid);
          if (!details || details.length === 0) {
            return;
          }
          let convC = 0;
          let convV = 0;
          details.forEach((detail) => {
            const raw = (detail.conversion_action_id || detail.conversion_action_name || '').toString().toLowerCase();
            if (!raw) return;
            if (!raw.includes('.')) {
              if (allowedActionTypes.has(raw)) {
                convC += Number(detail.conversions ?? 0);
                convV += Number(detail.conversions_value ?? 0);
              }
              return;
            }
            if (!allowedActionTypes.has(raw)) {
              return;
            }
            const canonical = metaCanonicalFromActionType(raw);
            if (canonical && canonicalAllowed.has(canonical)) {
              // Ya cubierta por su versión canónica, saltar para evitar doble conteo
              return;
            }
            convC += Number(detail.conversions ?? 0);
            convV += Number(detail.conversions_value ?? 0);
          });
          row.conversions = convC;
          row.conversions_value = convV;
          const cost = Number(row.cost ?? 0);
          row.cost_per_conversion = convC > 0 ? cost / convC : 0;
        });
      }
    }

    // Recalcular métricas agregadas para cards en display_currency
    const google_currency = baseTable.google?.account_currency || 'USD';
    const meta_currency = baseTable.meta?.account_currency || 'USD';
    const googleAgg = aggregateCampaignRows(googleBase);
    const metaAgg = aggregateCampaignRows(metaBase);

    const googleInv = convertToDisplayCurrency(googleAgg.investment, google_currency, displayCurrencyVal, rates);
    const googleCv = convertToDisplayCurrency(googleAgg.conversions_value, google_currency, displayCurrencyVal, rates);
    const metaInv = convertToDisplayCurrency(metaAgg.investment, meta_currency, displayCurrencyVal, rates);
    const metaCv = convertToDisplayCurrency(metaAgg.conversions_value, meta_currency, displayCurrencyVal, rates);
    const totalCost = googleInv + metaInv;
    const totalCv = googleCv + metaCv;

    const combinedForView = {
      combined: {
        investment: totalCost,
        cost: totalCost,
        currency: displayCurrencyVal,
        activeCampaigns: googleAgg.activeCampaigns + metaAgg.activeCampaigns,
        clicks: googleAgg.clicks + metaAgg.clicks,
        impressions: googleAgg.impressions + metaAgg.impressions,
        conversions: googleAgg.conversions + metaAgg.conversions,
        conversions_value: totalCv,
        roas: totalCost > 0 && totalCv > 0 ? totalCv / totalCost : null,
      },
      google_normalized: {
        investment: googleInv,
        cost: googleInv,
        currency: displayCurrencyVal,
        activeCampaigns: googleAgg.activeCampaigns,
        clicks: googleAgg.clicks,
        impressions: googleAgg.impressions,
        conversions: googleAgg.conversions,
        conversions_value: googleCv,
        roas: googleInv > 0 && googleCv > 0 ? googleCv / googleInv : googleAgg.roas ?? null,
        platform: 'google',
      },
      meta_normalized: {
        investment: metaInv,
        cost: metaInv,
        currency: displayCurrencyVal,
        activeCampaigns: metaAgg.activeCampaigns,
        clicks: metaAgg.clicks,
        impressions: metaAgg.impressions,
        conversions: metaAgg.conversions,
        conversions_value: metaCv,
        roas: metaInv > 0 && metaCv > 0 ? metaCv / metaInv : metaAgg.roas ?? null,
        platform: 'meta',
      },
      display_currency: displayCurrencyVal,
      exchange_rates: combinedData.exchange_rates || { USD: 1 },
    };

    const tableDataForView = {
      ...baseTable,
      google: baseTable.google
        ? { ...baseTable.google, campaign_metrics: googleBase }
        : baseTable.google,
      meta: baseTable.meta
        ? { ...baseTable.meta, campaign_metrics: metaBase }
        : baseTable.meta,
    };

    return { tableDataForView, combinedForView };
  }, [
    tableData,
    combinedData,
    filteredTableData,
    filteredCombinedView,
    hasConversionFilter,
    hasGoogleConversionFilter,
    hasMetaConversionFilter,
    filterConversionIds,
    filterConversionTypes,
    campaignConversionsDetail,
    normalizeGoogleConversionActionId,
    metaAllowedActionTypes,
    metaCanonicalFromActionType,
  ]);

  // Obtener totales desde combined analytics (como en el inicio)
  const totalInvestment = dataForCards?.combined?.investment || dataForCards?.combined?.cost || 0;
  const totalGoogleInvestment = dataForCards?.google_normalized?.investment || dataForCards?.google_normalized?.cost || 0;
  const totalMetaInvestment = dataForCards?.meta_normalized?.investment || dataForCards?.meta_normalized?.cost || 0;

  // Datos para gráficos comparativos
  const roasComparisonData = useMemo(() => {
    const googleROASRaw = coerceToNumber(dataForCards?.google_normalized?.roas);
    const metaROASRaw = coerceToNumber(dataForCards?.meta_normalized?.roas);

    const googleROAS = googleROASRaw !== null ? Number(googleROASRaw.toFixed(2)) : 0;
    const metaROAS = metaROASRaw !== null ? Number(metaROASRaw.toFixed(2)) : 0;
    
    return [
      { name: 'Google Ads', value: googleROAS },
      { name: 'Meta Ads', value: metaROAS }
    ];
  }, [dataForCards]);

  const investmentDistributionData = useMemo(() => {
    const total = totalGoogleInvestment + totalMetaInvestment;
    if (total === 0) {
      return [
        { name: 'Google Ads', value: 50, percentage: '50%' },
        { name: 'Meta Ads', value: 50, percentage: '50%' }
      ];
    }
    
    const googlePercentage = (totalGoogleInvestment / total) * 100;
    const metaPercentage = (totalMetaInvestment / total) * 100;
    
    return [
      { name: 'Google Ads', value: totalGoogleInvestment, percentage: `${googlePercentage.toFixed(1)}%` },
      { name: 'Meta Ads', value: totalMetaInvestment, percentage: `${metaPercentage.toFixed(1)}%` }
    ];
  }, [totalGoogleInvestment, totalMetaInvestment]);

  // Colores para los gráficos
  const COLORS = {
    google: '#4285F4', // Azul
    meta: '#10B981'    // Verde
  };

  // Mismas métricas que las cards (mismo id y title) para el gráfico de evolución
  const LINE_CHART_METRICS = useMemo(() => [
    { key: 'Clicks', label: 'Clicks', color: '#3B82F6' },
    { key: 'Costo', label: 'Costo', color: '#10B981' },
    { key: 'CPC', label: 'CPC', color: '#8B5CF6' },
    { key: 'Impresiones', label: 'Impresiones', color: '#A855F7' },
    { key: 'Conversiones', label: 'Conversiones', color: '#F59E0B' },
    { key: 'CPA', label: 'CPA', color: '#EF4444' },
    { key: 'CTR', label: 'CTR', color: '#06B6D4' },
    { key: 'ROAS', label: 'ROAS', color: '#EC4899' }
  ], []);

  const evolutionChartData = useMemo(() => {
    if (!dailyData || dailyData.length === 0) return [];
    const totalUnfilteredConv = dailyData.reduce((s, d) => s + (Number(d.combined?.conversions) || 0), 0);
    const totalUnfilteredValue = dailyData.reduce((s, d) => s + (Number(d.combined?.conversions_value) || 0), 0);
    const totalFilteredConv = hasConversionFilter ? (Number(dataForCards?.combined?.conversions) || 0) : null;
    const totalFilteredValue = hasConversionFilter ? (Number(dataForCards?.combined?.conversions_value) || 0) : null;
    const scaleConv = hasConversionFilter && totalFilteredConv != null && totalUnfilteredConv > 0 ? totalFilteredConv / totalUnfilteredConv : 1;
    const scaleValue = hasConversionFilter && totalFilteredValue != null && totalUnfilteredValue > 0 ? totalFilteredValue / totalUnfilteredValue : 1;

    return dailyData.map((d) => {
      const c = d.combined || {};
      const cost = c.investment ?? c.cost ?? 0;
      const clicks = c.clicks ?? 0;
      const impressions = c.impressions ?? 0;
      const conversionsRaw = Number(c.conversions) || 0;
      const valueRaw = Number(c.conversions_value) || 0;
      const conversions = conversionsRaw * scaleConv;
      const conversionsValue = valueRaw * scaleValue;
      return {
        date: d.date,
        dateLabel: formatDate(new Date(d.date + 'T12:00:00'), 'daily'),
        Clicks: clicks,
        Costo: cost,
        CPC: clicks > 0 ? cost / clicks : 0,
        Impresiones: impressions,
        Conversiones: conversions,
        CPA: conversions > 0 ? cost / conversions : 0,
        CTR: impressions > 0 ? (clicks / impressions) * 100 : 0,
        ROAS: cost > 0 && conversionsValue > 0 ? conversionsValue / cost : (c.roas ?? 0)
      };
    });
  }, [dailyData, hasConversionFilter, dataForCards?.combined?.conversions, dataForCards?.combined?.conversions_value]);

  // Calcular métricas automáticamente
  const totalClicks = dataForCards?.combined?.clicks || 0;
  const totalImpressions = dataForCards?.combined?.impressions || 0;
  const totalConversions = dataForCards?.combined?.conversions || 0;

  // Cálculos de métricas
  const calculatedCPC = totalClicks > 0 ? Number((totalInvestment / totalClicks).toFixed(2)) : 0;
  const calculatedCTR = totalImpressions > 0 ? Number(((totalClicks / totalImpressions) * 100).toFixed(2)) : 0;
  const calculatedCPA = totalConversions > 0 ? Number((totalInvestment / totalConversions).toFixed(2)) : 0;
  const calculatedROAS = (() => {
    const roasNum = coerceToNumber(dataForCards?.combined?.roas);
    return roasNum !== null ? Number(roasNum.toFixed(2)) : null;
  })();

  // Metric cards configuration con tooltips en español
  const metricCardsConfig = useMemo(() => [
    {
      id: 'Costo',
      title: 'Costo',
      value: totalInvestment,
      icon: DollarSign,
      prefix: displayCurrency === 'USD' ? '$' : '$ ',
      suffix: displayCurrency === 'UYU' ? ' UYU' : '',
      tooltip: 'Inversión total en todas tus campañas publicitarias.\nRepresenta el gasto acumulado en el período seleccionado.',
      formatAsInteger: false
    },
    {
      id: 'Impresiones',
      title: 'Impresiones',
      value: totalImpressions,
      icon: Eye,
      suffix: '',
      tooltip: 'Número total de veces que se mostraron tus anuncios.\nIndica el alcance visual de tus campañas.',
      formatAsInteger: true
    },
    {
      id: 'Clicks',
      title: 'Clicks',
      value: totalClicks,
      icon: MousePointerClick,
      suffix: '',
      tooltip: 'Número total de clics que recibieron tus anuncios.\nIndica cuántas veces los usuarios interactuaron con tus campañas.',
      formatAsInteger: true
    },
    {
      id: 'Conversiones',
      title: 'Conversiones',
      value: totalConversions,
      icon: Users,
      suffix: '',
      tooltip: 'Número total de conversiones generadas.\nRepresenta las acciones valiosas completadas por los usuarios (ventas, registros, etc.).',
      formatAsInteger: true
    },
    {
      id: 'ROAS',
      title: 'ROAS',
      value: calculatedROAS,
      icon: TrendingUp,
      suffix: 'x',
      tooltip: 'Return On Ad Spend (ROAS).\nMuestra cuántos dólares generas por cada dólar invertido en publicidad.\nEjemplo: ROAS de 3x significa que generas $3 por cada $1 invertido.',
      formatAsInteger: false
    },
    {
      id: 'CPA',
      title: 'CPA',
      value: calculatedCPA,
      icon: TrendingDown,
      prefix: displayCurrency === 'USD' ? '$' : '$ ',
      suffix: displayCurrency === 'UYU' ? ' UYU' : '',
      tooltip: 'Costo Por Adquisición (CPA).\nCálculo: Inversión total ÷ Total de conversiones.\nMuestra cuánto inviertes para obtener cada conversión.',
      formatAsInteger: false
    },
    {
      id: 'CTR',
      title: 'CTR',
      value: calculatedCTR,
      icon: Percent,
      suffix: '%',
      tooltip: 'Click-Through Rate (CTR).\nCálculo: (Clics ÷ Impresiones) × 100.\nIndica el porcentaje de personas que hicieron clic después de ver tu anuncio.',
      formatAsInteger: false
    },
    {
      id: 'CPC',
      title: 'CPC',
      value: calculatedCPC,
      icon: Target,
      prefix: displayCurrency === 'USD' ? '$' : '$ ',
      suffix: displayCurrency === 'UYU' ? ' UYU' : '',
      tooltip: 'Costo Por Clic (CPC).\nCálculo: Inversión total ÷ Total de clics.\nMuestra cuánto pagas en promedio por cada clic.',
      formatAsInteger: false
    }
  ], [
    totalClicks,
    totalInvestment,
    calculatedCPC,
    totalImpressions,
    totalConversions,
    calculatedCPA,
    calculatedCTR,
    calculatedROAS,
    displayCurrency
  ]);

  // Función para cambiar la métrica en una posición específica de card
  const handleMetricChange = (cardIndex, newMetricId) => {
    setCardMetrics(prev => {
      const updated = [...prev];
      updated[cardIndex] = newMetricId;
      return updated;
    });
  };

  // Helper function para formatear valores de métricas
  const formatMetricValue = (value, formatAsInteger) => {
    if (value === null || value === undefined) return 0;

    if (formatAsInteger) {
      return Math.round(value);
    }

    return value;
  };

  // Si no hay ninguna cuenta conectada, mostrar título, descripción y estado vacío con acciones
  if (!hasAnyConnection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
        <div className="max-w-7xl mx-auto p-6">
          <NoAdsConnectedSection
            connectGoogleAds={connectGoogleAds}
            startMetaOAuth={startMetaOAuth}
            activePlatform={'all'}
            activeGoogleAccount={null}
            activeMetaAccount={null}
            context={'analytics'}
          />
        </div>
      </div>
    );
  }

  // Mobile Platform Card Component
  const MobilePlatformCard = ({ platform, data, formatCurrency: currencyFormatter, displayCurrency }) => {
    if (!data) return null;

    const platformName = platform === 'google' ? 'Google Ads' : 'Meta Ads';
    const PlatformLogo = platform === 'google' ? GoogleAdsLogo : MetaAdsLogo;
    const platformBg = platform === 'google' ? 'bg-blue-500/20' : 'bg-purple-500/20';

    const investment = data.investment || data.cost || 0;
    const conversions = data.conversions || 0;
    const clicks = data.clicks || 0;
    const impressions = data.impressions || 0;
    const roasRaw = coerceToNumber(data.roas);
    const roas = roasRaw !== null ? Number(roasRaw.toFixed(2)) : null;

    const cpa = conversions > 0 ? investment / conversions : 0;
    const cpc = clicks > 0 ? investment / clicks : 0;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    const cpm = impressions > 0 ? investment / (impressions / 1000) : 0;

    return (
      <div className="backdrop-blur-md border border-white/20 rounded-xl p-4 space-y-3">
        {/* Header with platform badge */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${platformBg} border border-white/20`}>
            <PlatformLogo />
          </div>
          <span className="text-white font-semibold text-sm">{platformName}</span>
        </div>

        {/* Primary metrics (3 columns with emphasized bg) */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/15 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/70 text-xs mb-1">Gasto</p>
            <p className="text-white font-bold text-sm">{currencyFormatter(investment, displayCurrency)}</p>
          </div>
          <div className="bg-white/15 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/70 text-xs mb-1">Conv.</p>
            <p className="text-white font-bold text-sm">{conversions.toFixed(0)}</p>
          </div>
          <div className="bg-white/15 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/70 text-xs mb-1">ROAS</p>
            <p className="text-white font-bold text-sm">{roas !== null ? roas.toFixed(2) : '0.00'}</p>
          </div>
        </div>

        {/* Secondary metrics (3 columns) */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/5 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/60 text-xs mb-1">CPA</p>
            <p className="text-white font-semibold text-xs">{currencyFormatter(cpa, displayCurrency)}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/60 text-xs mb-1">CPC</p>
            <p className="text-white font-semibold text-xs">{cpc > 0 ? currencyFormatter(cpc, displayCurrency) : 'N/A'}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/60 text-xs mb-1">CPM</p>
            <p className="text-white font-semibold text-xs">{cpm > 0 ? currencyFormatter(cpm, displayCurrency) : 'N/A'}</p>
          </div>
        </div>

        {/* Tertiary metrics (3 columns) */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/5 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/60 text-xs mb-1">Clicks</p>
            <p className="text-white font-semibold text-xs">{clicks.toLocaleString()}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/60 text-xs mb-1">CTR</p>
            <p className="text-white font-semibold text-xs">{ctr > 0 ? `${ctr.toFixed(2)}%` : 'N/A'}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2.5 text-center border border-white/10">
            <p className="text-white/60 text-xs mb-1">Impresiones</p>
            <p className="text-white font-semibold text-xs">{impressions.toLocaleString()}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-20 sm:pt-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:p-6">

        {/* Barra de filtros sticky (período, plataforma, campañas) */}
        <AnalyticsStickyFilterBar />
        </div>

        {/* Contenedor con margen y padding para reportes y tablas */}
        <div className="max-w-8xl mx-auto px-2 xs:px-3 sm:px-4 lg:px-6 pb-4 sm:pb-6">

        {/* Metric Cards Grid */}
        <div className="w-full mb-8">
          <div className="mb-6">
            {loading ? (
              // Skeleton igual que AnalyticsMetricCard (mismo diseño cuando cargado)
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                {cardMetrics.map((_, index) => (
                  <div
                    key={index}
                    className="backdrop-blur-xl bg-slate-800/90 border border-white/20 rounded-lg sm:rounded-xl md:rounded-xl p-1.5 sm:p-2 md:p-3 flex flex-col items-center justify-center space-y-0.5 sm:space-y-1 md:space-y-2 shadow-lg"
                  >
                    <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded-full bg-white/20 animate-pulse" />
                    <div className="text-center w-full">
                      <div className="h-2.5 sm:h-3 md:h-3.5 bg-white/20 rounded w-3/4 mx-auto mb-0.5 sm:mb-1 animate-pulse" />
                      <div className="h-3 sm:h-3.5 md:h-4 bg-white/20 rounded w-1/2 mx-auto animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                {cardMetrics.map((metricId, index) => {
                  // Encontrar la configuración de la métrica actual
                  const metric = metricCardsConfig.find(m => m.id === metricId);
                  if (!metric) return null;

                  return (
                    <AnalyticsMetricCard
                      key={`card-${index}`}
                      title={metric.title}
                      value={formatMetricValue(metric.value, metric.formatAsInteger)}
                      icon={metric.icon}
                      prefix={metric.prefix || ''}
                      suffix={metric.suffix || ''}
                      loading={false}
                      tooltip={metric.tooltip}
                      availableMetrics={metricCardsConfig}
                      onMetricChange={(newMetricId) => handleMetricChange(index, newMetricId)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Gráfico de evolución por día */}
        {loading ? (
          <EvolutionChartSkeleton />
        ) : (
          <div className="mt-6 sm:mt-8 md:mt-12">
            <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-lg sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg transition-all duration-500 ease-out">
              <div className="relative z-10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Evolución por día</h3>
                    <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                    <label className="shrink-0">
                      <select
                        value={selectedMetric1}
                        onChange={(e) => setSelectedMetric1(e.target.value)}
                        className="rounded-lg border bg-white/10 py-2 px-3 text-sm min-w-[140px] transition-colors"
                        style={{
                          color: '#10B981',
                          borderColor: '#10B981'
                        }}
                      >
                        {LINE_CHART_METRICS.map((m) => (
                          <option key={m.key} value={m.key} className="bg-slate-800 text-white">
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="shrink-0">
                      <select
                        value={selectedMetric2}
                        onChange={(e) => setSelectedMetric2(e.target.value)}
                        className="rounded-lg border bg-white/10 py-2 px-3 text-sm min-w-[140px] transition-colors"
                        style={{
                          color: '#3B82F6',
                          borderColor: '#3B82F6'
                        }}
                      >
                        {LINE_CHART_METRICS.map((m) => (
                          <option key={m.key} value={m.key} className="bg-slate-800 text-white">
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
                {evolutionChartData.length === 0 ? (
                  <p className="text-white/70 text-center py-8">Selecciona un rango de fechas para ver la evolución.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 320}>
                    <LineChart data={evolutionChartData} margin={{ top: 10, right: 50, left: 50, bottom: 5 }}>
                      <XAxis
                        dataKey="dateLabel"
                        tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                        tickLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                      />
                      <YAxis
                        yAxisId="left"
                        orientation="left"
                        tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                        tickLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                        tickFormatter={(v) => (Number(v) >= 1000 ? `${(Number(v) / 1000).toFixed(1)}k` : Number(v).toLocaleString())}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                        tickLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                        tickFormatter={(v) => (Number(v) >= 1000 ? `${(Number(v) / 1000).toFixed(1)}k` : Number(v).toLocaleString())}
                      />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}
                        labelStyle={{ color: 'rgba(255,255,255,0.9)' }}
                        formatter={(value, name) => {
                          const metric = LINE_CHART_METRICS.find((m) => m.key === name);
                          return [Number(value).toLocaleString(), metric ? metric.label : name];
                        }}
                        labelFormatter={(label) => `Día: ${label}`}
                      />
                      <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.8)' }} iconType="circle" />
                      {[selectedMetric1, selectedMetric2]
                        .filter((key, i, arr) => arr.indexOf(key) === i)
                        .map((key, index) => {
                          const m = LINE_CHART_METRICS.find((met) => met.key === key);
                          if (!m) return null;
                          const fixedLineColor = index === 0 ? '#10B981' : '#3B82F6';
                          return (
                            <Line
                              key={m.key}
                              yAxisId={index === 0 ? 'left' : 'right'}
                              type="monotone"
                              dataKey={m.key}
                              name={m.label}
                              stroke={fixedLineColor}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          );
                        })}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tabla desglose por plataforma (Google / Meta) */}
        <div className="w-full mb-8">
        {loading ? (
          <PlatformTableSkeleton />
        ) : (
          <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl hover:bg-gradient-to-br hover:from-slate-700/90 hover:via-slate-800/90 hover:to-indigo-800/90 hover:border-white/30 transition-all duration-500 ease-out mt-8 sm:mt-12">
          <div className="relative z-10">
            {isMobile ? (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-white mb-4">Desglose por Plataforma</h2>
                <MobilePlatformCard
                  platform="google"
                  data={dataForCards?.google_normalized}
                  formatCurrency={formatCurrency}
                  displayCurrency={displayCurrency}
                />
                <MobilePlatformCard
                  platform="meta"
                  data={dataForCards?.meta_normalized}
                  formatCurrency={formatCurrency}
                  displayCurrency={displayCurrency}
                />
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg">
                <table className="w-full min-w-full md:min-w-[700px]">
                <thead>
                  <tr className="border-b border-white/30">
                    <th className="text-left py-3 px-4 font-medium text-white">Plataforma</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Gasto Total</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Conversiones</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CPA</th>
                    <th className="text-center py-3 px-4 font-medium text-white">ROAS</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Clicks</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CPC</th>
                    <th className="text-center py-3 px-4 font-medium text-white">Impresiones</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CPM</th>
                    <th className="text-center py-3 px-4 font-medium text-white">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {/* Google Ads Row */}
                  <tr className="hover:bg-white/10 transition-colors">
                    <td className="py-4 px-4 font-medium text-white">
                      <div className="flex items-center gap-2">
                        <GoogleAdsLogo />
                        {auth?.user?.google_ads_customer_id && (
                          <span className="text-xs text-white/70">({auth.user.google_ads_customer_id})</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center font-semibold text-white">
                      <div>
                        {formatCurrency(totalGoogleInvestment, displayCurrency)}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalGoogleInvestment, comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost))}`}>
                            {formatPercentageChange(calculatePercentageChange(totalGoogleInvestment, comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(dataForCards?.google_normalized?.conversions || 0).toFixed(1).replace('.0', '')}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(dataForCards?.google_normalized?.conversions || 0, comparisonData?.google_normalized?.conversions))}`}>
                            {formatPercentageChange(calculatePercentageChange(dataForCards?.google_normalized?.conversions || 0, comparisonData?.google_normalized?.conversions))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const googleConversions = dataForCards?.google_normalized?.conversions || 0;
                          const googleCPA = googleConversions > 0 ? Number((totalGoogleInvestment / googleConversions).toFixed(2)) : 0;
                          return formatCurrency(googleCPA, displayCurrency);
                        })()}
                        {comparisonData && (() => {
                          const googleConversions = dataForCards?.google_normalized?.conversions || 0;
                          const googleCPA = googleConversions > 0 ? Number((totalGoogleInvestment / googleConversions).toFixed(2)) : 0;
                          const prevGoogleConversions = comparisonData?.google_normalized?.conversions || 0;
                          const prevGoogleInvestment = comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost || 0;
                          const prevGoogleCPA = prevGoogleConversions > 0 ? Number((prevGoogleInvestment / prevGoogleConversions).toFixed(2)) : 0;
                          return googleCPA > 0 && prevGoogleCPA > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(googleCPA, prevGoogleCPA), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(googleCPA, prevGoogleCPA))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const googleROASRaw = coerceToNumber(dataForCards?.google_normalized?.roas);
                          const googleROAS = googleROASRaw !== null ? Number(googleROASRaw.toFixed(2)) : null;
                          return googleROAS !== null ? googleROAS.toFixed(2) : '0.00';
                        })()}
                        {comparisonData && (() => {
                          const googleROASRaw = coerceToNumber(dataForCards?.google_normalized?.roas);
                          const googleROAS = googleROASRaw !== null ? Number(googleROASRaw.toFixed(2)) : null;
                          const prevGoogleROASRaw = coerceToNumber(comparisonData?.google_normalized?.roas);
                          const prevGoogleROAS = prevGoogleROASRaw !== null ? Number(prevGoogleROASRaw.toFixed(2)) : null;
                          return googleROAS !== null && prevGoogleROAS !== null ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(googleROAS, prevGoogleROAS), false)}`}>
                              {formatPercentageChange(calculatePercentageChange(googleROAS, prevGoogleROAS))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {dataForCards?.google_normalized?.clicks?.toLocaleString() || '0'}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(dataForCards?.google_normalized?.clicks || 0, comparisonData?.google_normalized?.clicks))}`}>
                            {formatPercentageChange(calculatePercentageChange(dataForCards?.google_normalized?.clicks || 0, comparisonData?.google_normalized?.clicks))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const googleClicks = dataForCards?.google_normalized?.clicks || 0;
                          const googleCPC = googleClicks > 0 ? Number((totalGoogleInvestment / googleClicks).toFixed(2)) : 0;
                          return googleCPC > 0 ? formatCurrency(googleCPC, displayCurrency) : 'N/A';
                        })()}
                        {comparisonData && (() => {
                          const googleClicks = dataForCards?.google_normalized?.clicks || 0;
                          const googleCPC = googleClicks > 0 ? Number((totalGoogleInvestment / googleClicks).toFixed(2)) : 0;
                          const prevGoogleClicks = comparisonData?.google_normalized?.clicks || 0;
                          const prevGoogleInvestment = comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost || 0;
                          const prevGoogleCPC = prevGoogleClicks > 0 ? Number((prevGoogleInvestment / prevGoogleClicks).toFixed(2)) : 0;
                          return googleCPC > 0 && prevGoogleCPC > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(googleCPC, prevGoogleCPC), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(googleCPC, prevGoogleCPC))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {dataForCards?.google_normalized?.impressions?.toLocaleString() || '0'}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(dataForCards?.google_normalized?.impressions || 0, comparisonData?.google_normalized?.impressions))}`}>
                            {formatPercentageChange(calculatePercentageChange(dataForCards?.google_normalized?.impressions || 0, comparisonData?.google_normalized?.impressions))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const googleImpressions = dataForCards?.google_normalized?.impressions || 0;
                          const googleCPM = googleImpressions > 0 ? Number((totalGoogleInvestment / (googleImpressions / 1000)).toFixed(2)) : 0;
                          return googleCPM > 0 ? formatCurrency(googleCPM, displayCurrency) : 'N/A';
                        })()}
                        {comparisonData && (() => {
                          const googleImpressions = dataForCards?.google_normalized?.impressions || 0;
                          const googleCPM = googleImpressions > 0 ? Number((totalGoogleInvestment / (googleImpressions / 1000)).toFixed(2)) : 0;
                          const prevGoogleImpressions = comparisonData?.google_normalized?.impressions || 0;
                          const prevGoogleInvestment = comparisonData?.google_normalized?.investment || comparisonData?.google_normalized?.cost || 0;
                          const prevGoogleCPM = prevGoogleImpressions > 0 ? Number((prevGoogleInvestment / (prevGoogleImpressions / 1000)).toFixed(2)) : 0;
                          return googleCPM > 0 && prevGoogleCPM > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(googleCPM, prevGoogleCPM), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(googleCPM, prevGoogleCPM))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const googleClicks = dataForCards?.google_normalized?.clicks || 0;
                          const googleImpressions = dataForCards?.google_normalized?.impressions || 0;
                          const googleCTR = googleImpressions > 0 ? Number(((googleClicks / googleImpressions) * 100).toFixed(2)) : 0;
                          return googleCTR > 0 ? `${googleCTR}%` : 'N/A';
                        })()}
                        {comparisonData && (() => {
                          const googleClicks = dataForCards?.google_normalized?.clicks || 0;
                          const googleImpressions = dataForCards?.google_normalized?.impressions || 0;
                          const googleCTR = googleImpressions > 0 ? Number(((googleClicks / googleImpressions) * 100).toFixed(2)) : 0;
                          const prevGoogleClicks = comparisonData?.google_normalized?.clicks || 0;
                          const prevGoogleImpressions = comparisonData?.google_normalized?.impressions || 0;
                          const prevGoogleCTR = prevGoogleImpressions > 0 ? Number(((prevGoogleClicks / prevGoogleImpressions) * 100).toFixed(2)) : 0;
                          return googleCTR > 0 && prevGoogleCTR > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(googleCTR, prevGoogleCTR), false)}`}>
                              {formatPercentageChange(calculatePercentageChange(googleCTR, prevGoogleCTR))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                  </tr>

                  {/* Meta Ads Row */}
                  <tr className="hover:bg-white/10 transition-colors">
                    <td className="py-4 px-4 font-medium text-white">
                      <div className="flex items-center gap-2">
                        <MetaAdsLogo />
                        {auth?.user?.meta_ad_account_id && (
                          <span className="text-xs text-white/70">({auth.user.meta_ad_account_id})</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center font-semibold text-white">
                      <div>
                        {formatCurrency(totalMetaInvestment, displayCurrency)}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(totalMetaInvestment, comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost))}`}>
                            {formatPercentageChange(calculatePercentageChange(totalMetaInvestment, comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(dataForCards?.meta_normalized?.conversions || 0).toFixed(1).replace('.0', '')}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(dataForCards?.meta_normalized?.conversions || 0, comparisonData?.meta_normalized?.conversions))}`}>
                            {formatPercentageChange(calculatePercentageChange(dataForCards?.meta_normalized?.conversions || 0, comparisonData?.meta_normalized?.conversions))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const metaConversions = dataForCards?.meta_normalized?.conversions || 0;
                          const metaCPA = metaConversions > 0 ? Number((totalMetaInvestment / metaConversions).toFixed(2)) : 0;
                          return formatCurrency(metaCPA, displayCurrency);
                        })()}
                        {comparisonData && (() => {
                          const metaConversions = dataForCards?.meta_normalized?.conversions || 0;
                          const metaCPA = metaConversions > 0 ? Number((totalMetaInvestment / metaConversions).toFixed(2)) : 0;
                          const prevMetaConversions = comparisonData?.meta_normalized?.conversions || 0;
                          const prevMetaInvestment = comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost || 0;
                          const prevMetaCPA = prevMetaConversions > 0 ? Number((prevMetaInvestment / prevMetaConversions).toFixed(2)) : 0;
                          return metaCPA > 0 && prevMetaCPA > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(metaCPA, prevMetaCPA), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(metaCPA, prevMetaCPA))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const metaROASRaw = coerceToNumber(dataForCards?.meta_normalized?.roas);
                          const metaROAS = metaROASRaw !== null ? Number(metaROASRaw.toFixed(2)) : null;
                          return metaROAS !== null ? metaROAS.toFixed(2) : '0.00';
                        })()}
                        {comparisonData && (() => {
                          const metaROASRaw = coerceToNumber(dataForCards?.meta_normalized?.roas);
                          const metaROAS = metaROASRaw !== null ? Number(metaROASRaw.toFixed(2)) : null;
                          const prevMetaROASRaw = coerceToNumber(comparisonData?.meta_normalized?.roas);
                          const prevMetaROAS = prevMetaROASRaw !== null ? Number(prevMetaROASRaw.toFixed(2)) : null;
                          return metaROAS !== null && prevMetaROAS !== null ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(metaROAS, prevMetaROAS), false)}`}>
                              {formatPercentageChange(calculatePercentageChange(metaROAS, prevMetaROAS))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {dataForCards?.meta_normalized?.clicks?.toLocaleString() || '0'}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(dataForCards?.meta_normalized?.clicks || 0, comparisonData?.meta_normalized?.clicks))}`}>
                            {formatPercentageChange(calculatePercentageChange(dataForCards?.meta_normalized?.clicks || 0, comparisonData?.meta_normalized?.clicks))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const metaClicks = dataForCards?.meta_normalized?.clicks || 0;
                          const metaCPC = metaClicks > 0 ? Number((totalMetaInvestment / metaClicks).toFixed(2)) : 0;
                          return metaCPC > 0 ? formatCurrency(metaCPC, displayCurrency) : 'N/A';
                        })()}
                        {comparisonData && (() => {
                          const metaClicks = dataForCards?.meta_normalized?.clicks || 0;
                          const metaCPC = metaClicks > 0 ? Number((totalMetaInvestment / metaClicks).toFixed(2)) : 0;
                          const prevMetaClicks = comparisonData?.meta_normalized?.clicks || 0;
                          const prevMetaInvestment = comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost || 0;
                          const prevMetaCPC = prevMetaClicks > 0 ? Number((prevMetaInvestment / prevMetaClicks).toFixed(2)) : 0;
                          return metaCPC > 0 && prevMetaCPC > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(metaCPC, prevMetaCPC), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(metaCPC, prevMetaCPC))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {dataForCards?.meta_normalized?.impressions?.toLocaleString() || '0'}
                        {comparisonData && (
                          <div className={`text-xs ${getChangeColor(calculatePercentageChange(dataForCards?.meta_normalized?.impressions || 0, comparisonData?.meta_normalized?.impressions))}`}>
                            {formatPercentageChange(calculatePercentageChange(dataForCards?.meta_normalized?.impressions || 0, comparisonData?.meta_normalized?.impressions))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const metaImpressions = dataForCards?.meta_normalized?.impressions || 0;
                          const metaCPM = metaImpressions > 0 ? Number((totalMetaInvestment / (metaImpressions / 1000)).toFixed(2)) : 0;
                          return metaCPM > 0 ? formatCurrency(metaCPM, displayCurrency) : 'N/A';
                        })()}
                        {comparisonData && (() => {
                          const metaImpressions = dataForCards?.meta_normalized?.impressions || 0;
                          const metaCPM = metaImpressions > 0 ? Number((totalMetaInvestment / (metaImpressions / 1000)).toFixed(2)) : 0;
                          const prevMetaImpressions = comparisonData?.meta_normalized?.impressions || 0;
                          const prevMetaInvestment = comparisonData?.meta_normalized?.investment || comparisonData?.meta_normalized?.cost || 0;
                          const prevMetaCPM = prevMetaImpressions > 0 ? Number((prevMetaInvestment / (prevMetaImpressions / 1000)).toFixed(2)) : 0;
                          return metaCPM > 0 && prevMetaCPM > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(metaCPM, prevMetaCPM), true)}`}>
                              {formatPercentageChange(calculatePercentageChange(metaCPM, prevMetaCPM))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-white/90">
                      <div>
                        {(() => {
                          const metaClicks = dataForCards?.meta_normalized?.clicks || 0;
                          const metaImpressions = dataForCards?.meta_normalized?.impressions || 0;
                          const metaCTR = metaImpressions > 0 ? Number(((metaClicks / metaImpressions) * 100).toFixed(2)) : 0;
                          return metaCTR > 0 ? `${metaCTR}%` : 'N/A';
                        })()}
                        {comparisonData && (() => {
                          const metaClicks = dataForCards?.meta_normalized?.clicks || 0;
                          const metaImpressions = dataForCards?.meta_normalized?.impressions || 0;
                          const metaCTR = metaImpressions > 0 ? Number(((metaClicks / metaImpressions) * 100).toFixed(2)) : 0;
                          const prevMetaClicks = comparisonData?.meta_normalized?.clicks || 0;
                          const prevMetaImpressions = comparisonData?.meta_normalized?.impressions || 0;
                          const prevMetaCTR = prevMetaImpressions > 0 ? Number(((prevMetaClicks / prevMetaImpressions) * 100).toFixed(2)) : 0;
                          return metaCTR > 0 && prevMetaCTR > 0 ? (
                            <div className={`text-xs ${getChangeColor(calculatePercentageChange(metaCTR, prevMetaCTR), false)}`}>
                              {formatPercentageChange(calculatePercentageChange(metaCTR, prevMetaCTR))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
                </div>
              )}
            </div>

            {/* Leyenda de comparación */}
            {comparisonData && getComparisonDates() && (
              <div className="mt-4">
                <div className="text-sm text-gray-600 text-center">
                  <span>📈 Comparando contra el período anterior: {getComparisonDates().start} - {getComparisonDates().end}</span>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Gráficos Comparativos - ROAS y Distribución de Inversión */}
        {loading ? (
          <RoasDistributionChartsSkeleton />
        ) : dataForCards ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-8 sm:mt-12 mb-8 sm:mb-12">
            {/* Gráfico de Barras - ROAS */}
            <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg transition-all duration-500 ease-out">
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-white mb-1">ROAS por Plataforma</h3>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mx-auto"></div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={roasComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                      tickLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                    />
                    <YAxis 
                      tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                      tickLine={{ stroke: 'rgba(255,255,255,0.3)' }}
                    />
                    <Tooltip 
                      contentStyle={{ display: 'none' }}
                      cursor={false}
                    />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {roasComparisonData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.name === 'Google Ads' ? COLORS.google : COLORS.meta} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Gráfico de Torta - Distribución de Inversión */}
            <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg transition-all duration-500 ease-out">
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-white mb-1">Distribución de Inversión</h3>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mx-auto"></div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={investmentDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.percentage}
                      outerRadius={100}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                      stroke="none"
                    >
                      {investmentDistributionData.map((entry, index) => {
                        const color = entry.name === 'Google Ads' ? COLORS.google : COLORS.meta;
                        return (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={color}
                            stroke={color}
                            strokeWidth={2}
                          />
                        );
                      })}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ display: 'none' }}
                      cursor={false}
                    />
                    <Legend 
                      wrapperStyle={{ color: 'rgba(255,255,255,0.8)' }}
                      iconType="circle"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        ) : null}

        {/* Tabla de Campañas con Impresiones - Mobile Optimized */}
        <div className="mt-8 sm:mt-12">
          <CampaignsWithImpressionsTable
            tableData={tableDataForView ?? filteredTableData ?? tableData}
            tableDataAdGroups={tableDataAdGroups}
            tableDataAds={tableDataAds}
            onRequestAdGroups={onRequestAdGroups}
            onRequestAds={onRequestAds}
            onApplyFilter={setFilterCampaignIds}
            onClearFilter={() => setFilterCampaignIds([])}
            hasCampaignFilter={(filterCampaignIds?.length ?? 0) > 0}
            getFullDataForExport={getFullDataForExport}
            displayCurrency={displayCurrency}
            exchangeRates={combinedData?.exchange_rates ?? {}}
            isGoogleConnected={isGoogleConnected}
            isMetaConnected={isMetaConnected}
            loading={loading}
            loadingAdGroups={loadingAdGroups}
            loadingAds={loadingAds}
            filterConversionIds={filterConversionIds}
            filterConversionTypes={filterConversionTypes}
            hasConversionFilter={hasConversionFilter}
            hasGoogleConversionFilter={hasGoogleConversionFilter}
            hasMetaConversionFilter={hasMetaConversionFilter}
          />
        </div>

        </div>
      </div>
    </>
  );
};

export default Analytics20;
